<template>
  <transition name="fade" @enter="handleEnter">
    <el-container v-if="isReady" class="new-collection-container" ref="container" :style="{ width: containerWidth, maxWidth: '100vw' }">
      <el-header>
        <div class="header-content">
          <h2>{{ isEditing ? 'Edit Course' : 'Create A New Course' }}</h2>
          <div class="button-group">
            <!-- <el-button type="primary" @click="savePublish">Publish</el-button> -->
            <!-- <el-button type="primary" @click="submitForm">Save</el-button> -->
            <el-button 
              type="primary" 
              plain
              @click="previewRead" 
              style="margin-left: 10px; border-color: #409eff; color: #409eff;"
            >Preview in Read Mode</el-button>
            
            <!-- <el-button @click="cancel">Cancel</el-button> -->
            <el-dropdown trigger="click" style="margin-left: 10px">
              <el-button type="text">
                <i class="el-icon-more" style="color: #409eff; transform: rotate(90deg);"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-if="isEditing">
                  <div @click="shareCollection" style="color: #409eff;">
                    <i class="el-icon-share"></i>
                    Share Course
                  </div>
                </el-dropdown-item>
                <el-dropdown-item v-if="isEditing">
                  <div @click="showTransferDialog" style="color: #409eff;">
                    <i class="el-icon-user"></i>
                    Transfer to Other User
                  </div>
                </el-dropdown-item>
                <el-dropdown-item v-if="isEditing">
                  <div @click="confirmDelete" style="color: #f56c6c;">
                    <i class="el-icon-delete"></i>
                    Delete Course
                  </div>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </el-header>
      <el-main>
        <div class="form-wrapper">
          <el-form ref="collectionForm" :model="collectionForm" :rules="rules">
            <el-tabs v-model="activeTab">
              <el-tab-pane label="Basic Info" name="info">
                <div class="top-section">
                  <!-- Cover image section -->
                  <div class="cover-section">
                    <div class="cover-upload">
                      <el-upload
                        ref="coverUpload"
                        class="cover-uploader"
                        action="/"
                        :show-file-list="false"
                        :before-upload="beforeCoverUpload"
                      >
                        <img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="cover-image">
                        <div v-else class="cover-placeholder">
                          <i class="el-icon-plus cover-uploader-icon"></i>
                          <div class="cover-label">Cover Image</div>
                        </div>
                      </el-upload>
                      <div class="cover-actions">
                        <el-button 
                          v-if="['Pro','Supreme'].includes(creator_functionality_level)" 
                          size="mini" 
                          type="primary" 
                          class="ai-generate-btn"
                          @click.stop="handleAITask('Collection Cover Generation')"
                        >
                          <i class="el-icon-magic-stick"></i>
                          Generate Cover by AI
                        </el-button>
                        <div class="cover-hint">Recommended size: 300x424px</div>
                      </div>
                    </div>
                  </div>

                  <!-- Title and description section -->
                  <div class="details-section">
                    <div class="title-section">
                      <label for="title">Title</label>
                      <input v-model="collectionForm.title" id="title" placeholder="Enter course title">
                    </div>

                    <div class="title-section">
                      <label for="sub_title">Subtitle</label>
                      <input v-model="collectionForm.sub_title" id="sub_title" placeholder="Enter course subtitle (optional)">
                    </div>
                    
                    <div class="description-section">
                      <div class="description-header">
                        <label for="description">Description</label>
                        <el-button
                          size="mini"
                          type="primary"
                          class="ai-polish-btn"
                          @click="handleAITask('Collection Description Polish')"
                        >
                          <i class="el-icon-magic-stick"></i>
                          AI Polish
                        </el-button>
                      </div>
                      <textarea
                        v-model="collectionForm.description"
                        id="description"
                        rows="8"
                        placeholder="Write a description for your course..."
                      ></textarea>
                    </div>
                    <div class="description-section">
                      <div class="description-header">
                        <label for="grand_take_away">Grand Takeaway</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          The highly distilled, single-most-important insight of the whole collection (2–3 sentences).
                        </span>
                      </div>
                      <textarea
                        v-model="collectionForm.grand_take_away"
                        id="grand_take_away"
                        rows="4"
                        placeholder="What is the one thing a reader should walk away from this collection having understood?"
                      ></textarea>
                    </div>
                    <div class="description-section">
                      <div class="description-header">
                        <label for="collection_knowledge">Certificate Knowledge Phrase</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          A short noun phrase describing what the course covers. Used on all three certificate types (Mastery / Completion / Record). e.g. "key concepts of cognitive psychology", "principles of sustainable business", "applications of AI in education".
                        </span>
                      </div>
                      <input
                        v-model="collectionForm.collection_knowledge"
                        id="collection_knowledge"
                        type="text"
                        maxlength="120"
                        placeholder="e.g. principles of sustainable business"
                      >
                    </div>

                    <!-- Walkaway artifacts — 3-5 teaser lines using artifact-count format -->
                    <div class="description-section">
                      <div class="description-header">
                        <label>What You'll Walk Away With</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          3–5 teaser lines. Format: [count] [artifact-noun] [what-for]. Use artifact nouns (models, frameworks, habits, traps…) — not content-volume nouns (articles, modules, sections).
                        </span>
                      </div>
                      <div v-if="(collectionForm.walkaway_artifacts || []).length === 0" style="margin-bottom: 8px;">
                        <el-button size="mini" @click="addWalkawayArtifact">+ Add walkaway line</el-button>
                      </div>
                      <div
                        v-for="(line, li) in collectionForm.walkaway_artifacts"
                        :key="li"
                        style="display: flex; gap: 8px; margin-bottom: 6px;"
                      >
                        <input
                          v-model="collectionForm.walkaway_artifacts[li]"
                          placeholder="e.g. 5 mental models for thinking with AI"
                          style="flex: 1;"
                        />
                        <el-button size="mini" type="danger" plain @click="removeWalkawayArtifact(li)">Remove</el-button>
                      </div>
                      <div v-if="(collectionForm.walkaway_artifacts || []).length > 0 && (collectionForm.walkaway_artifacts || []).length < 8">
                        <el-button size="mini" @click="addWalkawayArtifact">+ Add another line</el-button>
                      </div>
                    </div>

                    <!-- Author bio — short intro rendered in the subscriber "About the Curator" card -->
                    <div class="description-section">
                      <div class="description-header">
                        <label for="author_bio">About the Curator (short intro)</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          1–2 sentence introduction to the channel / editorial voice behind this collection. Rendered in the small "About the Curator" card at the bottom of the subscriber page.
                        </span>
                      </div>
                      <textarea
                        v-model="collectionForm.author_bio"
                        id="author_bio"
                        rows="3"
                        placeholder="A short introduction to the editorial voice behind this collection."
                      ></textarea>
                    </div>

                    <!-- Genre Tags — 1–2 free-form descriptive words about the collection's content paradigm -->
                    <div class="description-section">
                      <div class="description-header">
                        <label for="genre_tags">Genre Tags</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          Two best descriptive words for this collection's paradigm (e.g. Primer, Field Guide, Playbook, Manifesto, Reframe, Discipline, Workbook, Anthology, Paradigm, Companion, Toolkit, Reportage, Industry Map…). Free-form — type any phrase and press Enter; vector search will handle synonymy. Cap at 2.
                        </span>
                      </div>
                      <el-select
                        v-model="collectionForm.genre_tags"
                        multiple
                        filterable
                        allow-create
                        default-first-option
                        :multiple-limit="2"
                        placeholder="Type a genre and press Enter (e.g. 'Field Guide')"
                        style="width: 100%;"
                        id="genre_tags"
                      />
                    </div>

                    <!-- Gold Quotes — up to 3 pull quotes for marketing + rotating card on the public page -->
                    <div class="description-section">
                      <div class="description-header">
                        <label>Gold Quotes</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          Up to 3 marketable pull quotes. Each is an *insight* (sharp standalone sentence, social-card length) + a *detail* note that unpacks it in 2–3 sentences. Rotates on the subscriber page between Editor's Brief and About the Curator.
                        </span>
                      </div>
                      <div v-if="(collectionForm.gold_quotes || []).length === 0" style="margin-bottom: 8px;">
                        <el-button size="mini" @click="addGoldQuote">+ Add gold quote</el-button>
                      </div>
                      <div
                        v-for="(gq, gqi) in collectionForm.gold_quotes"
                        :key="gqi"
                        style="border: 1px solid #dcdfe6; border-radius: 4px; padding: 12px; margin-bottom: 8px;"
                      >
                        <textarea
                          v-model="gq.insight"
                          placeholder="Insight — the golden sentence (keep it under ~25 words; must stand alone as a social card)"
                          rows="2"
                          style="width: 100%; margin-bottom: 8px;"
                        ></textarea>
                        <textarea
                          v-model="gq.detail"
                          placeholder="Detail — 2–3 sentences that unpack the insight"
                          rows="3"
                          style="width: 100%; margin-bottom: 8px;"
                        ></textarea>
                        <div style="display: flex; justify-content: flex-end;">
                          <el-button size="mini" type="danger" plain @click="removeGoldQuote(gqi)">Remove</el-button>
                        </div>
                      </div>
                      <div v-if="(collectionForm.gold_quotes || []).length > 0 && (collectionForm.gold_quotes || []).length < 3">
                        <el-button size="mini" @click="addGoldQuote">+ Add another quote</el-button>
                      </div>
                    </div>

                    <!-- Key Questions — 3–5 critical questions the collection answers -->
                    <div class="description-section">
                      <div class="description-header">
                        <label>Key Questions</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          3–5 critical questions this collection answers. Sharp, curiosity-driven — designed for promotional copy and social cards.
                        </span>
                      </div>
                      <div v-if="(collectionForm.key_questions || []).length === 0" style="margin-bottom: 8px;">
                        <el-button size="mini" @click="addKeyQuestion">+ Add question</el-button>
                      </div>
                      <div
                        v-for="(q, qi) in collectionForm.key_questions"
                        :key="'kq-' + qi"
                        style="display: flex; gap: 8px; margin-bottom: 6px;"
                      >
                        <input
                          v-model="collectionForm.key_questions[qi]"
                          placeholder="e.g. Why do most people fail at self-assessment — and what does the research say?"
                          style="flex: 1;"
                        />
                        <el-button size="mini" type="danger" plain @click="removeKeyQuestion(qi)">Remove</el-button>
                      </div>
                      <div v-if="(collectionForm.key_questions || []).length > 0 && (collectionForm.key_questions || []).length < 5">
                        <el-button size="mini" @click="addKeyQuestion">+ Add another question</el-button>
                      </div>
                    </div>

                    <!-- Key Concepts — 5–10 concept-level knowledge phrases -->
                    <div class="description-section">
                      <div class="description-header">
                        <label>Key Concepts</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          5–10 concept-level knowledge phrases users will learn. Concise terms, not full sentences (e.g. "Performance Identity", "Feedback Integration Loop").
                        </span>
                      </div>
                      <div v-if="(collectionForm.key_concepts || []).length === 0" style="margin-bottom: 8px;">
                        <el-button size="mini" @click="addKeyConcept">+ Add concept</el-button>
                      </div>
                      <div
                        v-for="(c, ci) in collectionForm.key_concepts"
                        :key="'kc-' + ci"
                        style="display: flex; gap: 8px; margin-bottom: 6px;"
                      >
                        <input
                          v-model="collectionForm.key_concepts[ci]"
                          placeholder="e.g. Cognitive Bias Blind Spot"
                          style="flex: 1;"
                        />
                        <el-button size="mini" type="danger" plain @click="removeKeyConcept(ci)">Remove</el-button>
                      </div>
                      <div v-if="(collectionForm.key_concepts || []).length > 0 && (collectionForm.key_concepts || []).length < 10">
                        <el-button size="mini" @click="addKeyConcept">+ Add another concept</el-button>
                      </div>
                    </div>

                    <!-- SNS Cards — 12 pre-generated social media carousel cards -->
                    <div class="description-section">
                      <div class="description-header">
                        <label>SNS Promotion Cards</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          Pre-generated social media carousels for TikTok / IG / Xiaohongshu. 3 per type × 4 types (summary_pack, step_by_step, contrast, checklist). Each zooms into one specific post.
                        </span>
                      </div>
                      <div v-if="(collectionForm.sns_cards || []).length === 0" style="margin-bottom: 8px;">
                        <el-button size="mini" @click="addSnsCard">+ Add SNS card</el-button>
                      </div>
                      <div
                        v-for="(card, si) in collectionForm.sns_cards"
                        :key="'sns-' + si"
                        style="border: 1px solid #dcdfe6; border-radius: 4px; padding: 12px; margin-bottom: 10px;"
                      >
                        <div style="display: flex; gap: 8px; margin-bottom: 8px; align-items: center;">
                          <el-select v-model="card.card_type" size="mini" style="width: 160px;">
                            <el-option label="Summary Pack" value="summary_pack" />
                            <el-option label="Step by Step" value="step_by_step" />
                            <el-option label="Contrast" value="contrast" />
                            <el-option label="Checklist" value="checklist" />
                          </el-select>
                          <input
                            v-model="card.source_post"
                            placeholder="Source post title"
                            style="flex: 1;"
                          />
                          <el-button size="mini" type="danger" plain @click="removeSnsCard(si)">Remove</el-button>
                        </div>
                        <input
                          v-model="card.hook"
                          placeholder="Hook — attention-grabbing headline for first slide (≤15 words)"
                          style="width: 100%; margin-bottom: 8px;"
                        />
                        <div style="margin-bottom: 4px; font-size: 12px; color: #909399;">Slides (one point per slide, ≤40 words each):</div>
                        <div
                          v-for="(page, pi) in card.pages"
                          :key="'sns-' + si + '-p-' + pi"
                          style="display: flex; gap: 8px; margin-bottom: 4px;"
                        >
                          <span style="color: #909399; font-size: 12px; min-width: 16px; padding-top: 6px;">{{ pi + 1 }}</span>
                          <input
                            v-model="card.pages[pi]"
                            placeholder="Slide content"
                            style="flex: 1;"
                          />
                          <el-button size="mini" type="text" style="color: #f56c6c;" @click="removeSnsCardPage(si, pi)">×</el-button>
                        </div>
                        <el-button size="mini" @click="addSnsCardPage(si)" style="margin-bottom: 8px;">+ Add slide</el-button>
                        <input
                          v-model="card.cta"
                          placeholder="CTA — closing slide call-to-action"
                          style="width: 100%;"
                        />
                      </div>
                      <div v-if="(collectionForm.sns_cards || []).length > 0">
                        <el-button size="mini" @click="addSnsCard">+ Add another card</el-button>
                      </div>
                    </div>

                    <!-- Tracks — career/skill tracks this collection contributes credits toward -->
                    <div class="description-section">
                      <div class="description-header">
                        <label for="tracks">Tracks</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          Which career / skill tracks this collection contributes credits toward when learners complete it. Pick up to 3. (e.g. a collection on AI management maps to both Management and Artificial Intelligence.)
                        </span>
                      </div>
                      <el-select
                        v-model="collectionForm.track_ids"
                        multiple
                        filterable
                        :multiple-limit="3"
                        placeholder="Select up to 3 tracks"
                        style="width: 100%;"
                        :loading="tracksLoading"
                        id="tracks"
                      >
                        <el-option
                          v-for="t in availableTracks"
                          :key="trackIdString(t)"
                          :label="t.track_name"
                          :value="trackIdString(t)"
                        />
                      </el-select>
                    </div>

                    <!-- Editorial Review (B4a) — headline verdict + supporting body -->
                    <div class="description-section">
                      <div class="description-header">
                        <label>Editorial Review</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          Third-person editorial verdict (one pull-quote headline + 2–3 supporting sentences). Shown as a reviewer-style card on the public collection page.
                        </span>
                      </div>
                      <div v-if="(collectionForm.editorial_reviews || []).length === 0" style="margin-bottom: 8px;">
                        <el-button size="mini" @click="addEditorialReview">+ Add editorial review</el-button>
                      </div>
                      <div
                        v-for="(review, ri) in collectionForm.editorial_reviews"
                        :key="ri"
                        style="border: 1px solid #dcdfe6; border-radius: 4px; padding: 12px; margin-bottom: 8px;"
                      >
                        <div style="display: flex; gap: 8px; margin-bottom: 8px;">
                          <input
                            v-model="review.reviewer"
                            placeholder="Reviewer (e.g. LearningFirst Editorial Team)"
                            style="flex: 1;"
                          />
                          <input
                            v-model="review.reviewer_role"
                            placeholder="Role (optional, e.g. Guest Critic)"
                            style="flex: 1;"
                          />
                        </div>
                        <input
                          v-model="review.headline"
                          placeholder="Headline (the pull-quote sentence)"
                          style="width: 100%; margin-bottom: 8px;"
                        />
                        <textarea
                          v-model="review.body"
                          placeholder="Body — 2–3 sentences justifying the verdict"
                          rows="3"
                          style="width: 100%; margin-bottom: 8px;"
                        ></textarea>
                        <div style="display: flex; align-items: center; justify-content: space-between;">
                          <label style="font-size: 12px; color: #606266;">
                            <input type="checkbox" v-model="review.is_ai_generated" /> AI-generated (disclosure shown on the public page)
                          </label>
                          <el-button size="mini" type="danger" plain @click="removeEditorialReview(ri)">Remove</el-button>
                        </div>
                      </div>
                      <div v-if="(collectionForm.editorial_reviews || []).length > 0 && (collectionForm.editorial_reviews || []).length < 3">
                        <el-button size="mini" @click="addEditorialReview">+ Add another review</el-button>
                      </div>
                    </div>

                    <!-- Editorial Brief (B4b) — structured fit qualifier -->
                    <div class="description-section">
                      <div class="description-header">
                        <label>Editorial Brief</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          Structured fit qualifier. Three single-sentence answers that help a reader decide whether this collection is for them.
                        </span>
                      </div>
                      <div style="margin-bottom: 8px;">
                        <label style="font-size: 13px; color: #606266;">Who it's for</label>
                        <textarea
                          v-model="collectionForm.editorial_brief.who_its_for"
                          rows="2"
                          placeholder="Describe the ideal reader in 1 sentence."
                          style="width: 100%;"
                        ></textarea>
                      </div>
                      <div style="margin-bottom: 8px;">
                        <label style="font-size: 13px; color: #606266;">What stands out</label>
                        <textarea
                          v-model="collectionForm.editorial_brief.what_stands_out"
                          rows="2"
                          placeholder="What makes this collection distinctive from alternatives? 1–2 sentences."
                          style="width: 100%;"
                        ></textarea>
                      </div>
                      <div style="margin-bottom: 8px;">
                        <label style="font-size: 13px; color: #606266;">Read if</label>
                        <textarea
                          v-model="collectionForm.editorial_brief.read_if"
                          rows="2"
                          placeholder="A single 'Read if…' sentence that snaps the fit question into focus."
                          style="width: 100%;"
                        ></textarea>
                      </div>
                      <label style="font-size: 12px; color: #606266;">
                        <input type="checkbox" v-model="collectionForm.editorial_brief.is_ai_generated" /> AI-generated (disclosure shown on the public page)
                      </label>
                    </div>
                    <div class="version-section">
                      <label for="version">Version Identifier</label>
                      <input 
                        v-model="collectionForm.version_identifier" 
                        id="version" 
                        placeholder="Enter version identifier (if necessary)"
                      >
                    </div>
                    <div class="tag-section">
                      <label for="tags">Tags</label>
                      <div class="tags-input-container">
                        <div 
                          v-for="(tag, index) in collectionForm.tags" 
                          :key="index" 
                          class="tag-item"
                        >
                          {{ tag }}
                          <button @click.prevent.stop="removeTag(index)" type="button" class="remove-tag-btn">&times;</button>
                        </div>
                        <input 
                          v-model="currentTag" 
                          id="tags" 
                          placeholder="Add a tag, press Enter to add..."
                          @keydown.enter.prevent="addTag"
                          @keydown.backspace="handleBackspace"
                        >
                      </div>
                    </div>

                  </div>
                  <div class="save-section">
                    <el-button 
                      type="primary" 
                      size="medium"
                      @click="submitForm"
                      :loading="saving"
                    >
                      Save
                    </el-button>
                  </div>
                </div>
              </el-tab-pane>

              

              <el-tab-pane label="Groups" name="groups">
                <div class="content-header">
                  <h3>Card Groups</h3>
                  <div class="content-stats">
                    <span class="stat-item">
                      <i class="el-icon-folder"></i>
                      {{ collectionForm.groups.length }} Groups
                    </span>
                    <span class="stat-item">
                      <i class="el-icon-collection"></i>
                      {{ totalKnowits }} Stacks
                    </span>
                    <span class="stat-item">
                      <i class="el-icon-document"></i>
                      {{ collectionForm.content_count || 0 }} Cards
                    </span>
                  </div>
                </div>
                <div class="content-actions">
                  <el-button 
                    v-if="['Pro','Supreme'].includes(creator_functionality_level)"
                    size="small" 
                    type="primary" 
                    class="ai-structure-btn"
                    @click="handleAITask('Collection Structure Generation')"
                  >
                    <i class="el-icon-magic-stick"></i>
                    Get Structure from AI
                  </el-button>
                  <el-button 
                    size="small"
                    type="default"
                    @click="showReorderDialog"
                  >
                    <i class="el-icon-sort"></i>
                    Reorder Groups
                  </el-button>

                  <!-- Add AI Generation button for Supreme users -->
                  <el-button
                    v-if="creator_functionality_level === 'Supreme'"
                    size="small"
                    type="primary"
                    @click="showAIGenerationDialog"
                  >
                    <i class="el-icon-magic-stick"></i>
                    Arrange AI Generation for Stacks
                  </el-button>
                  <el-button type="default" size="small" @click="showBatchDeleteDialog">
                    <i class="el-icon-delete" style="color: red;"></i>
                    <span style="color: red;">Batch Delete Stacks</span>
                  </el-button>

                  <el-divider direction="vertical"></el-divider>

                  <el-button size="small" type="warning" plain @click="makeAllSubscribersOnly">
                    <i class="el-icon-lock"></i>
                    All Behind Paywall
                  </el-button>
                  <el-button size="small" plain @click="makeAllFree">
                    <i class="el-icon-unlock"></i>
                    All Free
                  </el-button>
                  <el-button size="small" :type="selectMode ? 'primary' : 'default'" @click="toggleSelectMode">
                    <i class="el-icon-finished"></i>
                    {{ selectMode ? 'Exit Select Mode' : 'Select Mode' }}
                  </el-button>
                  <template v-if="selectMode">
                    <el-button size="small" @click="selectAll">Select All</el-button>
                    <el-button size="small" @click="deselectAll">Deselect All</el-button>
                    <el-button size="small" type="warning" plain @click="makeSelectedSubscribersOnly" :disabled="selectedGroups.length === 0 && selectedKnowitsList.length === 0">
                      <i class="el-icon-lock"></i>
                      Lock Selected
                    </el-button>
                    <el-button size="small" plain @click="makeSelectedFree" :disabled="selectedGroups.length === 0 && selectedKnowitsList.length === 0">
                      <i class="el-icon-unlock"></i>
                      Unlock Selected
                    </el-button>
                  </template>

                </div>
                <div class="groups-container">
                  <!-- Groups List -->
                  <div class="groups-list">
                    <!-- Add insert button before the first group -->
                    <div class="insert-group-container">
                      <button 
                        class="insert-group-button"
                        @click="insertGroup(0)"
                      >
                        <i class="el-icon-plus"></i>
                        Add Group Here
                      </button>
                    </div>

                    <div 
                      v-for="(group, groupIndex) in collectionForm.groups" 
                      :key="groupIndex"
                      class="group-wrapper"
                    >
                      <div class="group-card">
                        <div class="group-card-header">
                          <span class="group-number">Group #{{ groupIndex + 1 }}</span>
                          <template v-if="group.isEditing">
                            <input
                              v-model="group.title"
                              placeholder="Group Title"
                              class="group-title-input"
                              @keydown.enter.prevent.stop="finishEditingTitle(group)"
                              @blur="handleGroupTitleBlur(group, $event)"
                              ref="titleInput"
                            >
                            <input
                              v-model="group.group_sub_title"
                              placeholder="Group Subtitle (optional)"
                              class="group-title-input group-subtitle-input"
                              @keydown.enter.prevent.stop="finishEditingTitle(group)"
                              @blur="handleGroupTitleBlur(group, $event)"
                            >
                          </template>
                          <span
                            v-else
                            class="group-title-label"
                            @dblclick="startEditingTitle(group)"
                          >
                            {{ group.title || 'Untitled Group' }}
                            <span v-if="group.group_sub_title" class="group-subtitle-label">— {{ group.group_sub_title }}</span>
                            <el-tag size="mini" type="info" style="margin-left: 8px;">
                              {{ getTotalCardsForGroup(group) }} stacks
                            </el-tag>
                          </span>
                          <el-tooltip :content="group.subscribers_only ? 'Click to make free' : 'Click to make subscribers only'" placement="top">
                            <el-button
                              :type="group.subscribers_only ? 'warning' : 'default'"
                              size="mini"
                              :icon="group.subscribers_only ? 'el-icon-lock' : 'el-icon-unlock'"
                              @click="toggleGroupPaywall(groupIndex)"
                              circle
                              class="paywall-toggle-btn"
                            ></el-button>
                          </el-tooltip>
                          <el-tag v-if="group.subscribers_only" type="warning" size="mini" effect="plain" style="margin-right: 4px;">
                            Subscribers Only
                          </el-tag>
                          <el-checkbox
                            v-if="selectMode"
                            :value="selectedGroups.includes(groupIndex)"
                            @change="toggleGroupSelection(groupIndex)"
                            style="margin-right: 4px;"
                          ></el-checkbox>
                          <el-button
                            type="danger"
                            size="mini"
                            icon="el-icon-delete"
                            @click="removeGroup(groupIndex)"
                            circle
                            class="delete-group-btn"
                          ></el-button>
                        </div>

                        <div class="group-card-content">
                          <div class="knowit-list">
                            <draggable 
                              v-model="group.knowits"
                              group="knowits"
                              handle=".drag-handle"
                            >
                              <div class="knowit-item" v-for="(knowit, knowitIndex) in group.knowits" :key="knowitIndex">
                                <div class="drag-handle">
                                  <i class="el-icon-rank"></i>
                                </div>
                                <div class="knowit-number">Stack #{{ knowitIndex + 1 }}</div>
                                <div class="knowit-title">
                                  {{ (knowit.post_info && knowit.post_info.title) || 'Untitled Stack' }}
                                  <el-tag size="mini" type="info" v-if="knowit.post_info && knowit.post_info.answers && knowit.post_info.answers.length">
                                    {{ knowit.post_info.answers.length }} cards
                                  </el-tag>
                                </div>
                                <el-tooltip :content="group.subscribers_only ? 'Locked by group' : (knowit.subscribers_only ? 'Click to make free' : 'Click to make subscribers only')" placement="top">
                                  <button
                                    class="knowit-lock-btn"
                                    :class="{ 'is-locked': knowit.subscribers_only || group.subscribers_only, 'is-inherited': group.subscribers_only }"
                                    @click.stop.prevent="!group.subscribers_only && toggleKnowitPaywall(groupIndex, knowitIndex)"
                                    type="button"
                                    :disabled="group.subscribers_only"
                                  >
                                    <i :class="(knowit.subscribers_only || group.subscribers_only) ? 'el-icon-lock' : 'el-icon-unlock'"></i>
                                  </button>
                                </el-tooltip>
                                <el-checkbox
                                  v-if="selectMode"
                                  :value="isKnowitSelected(groupIndex, knowitIndex)"
                                  @change="toggleKnowitSelection(groupIndex, knowitIndex)"
                                  style="margin-right: 4px;"
                                ></el-checkbox>
                                <button
                                  class="knowit-edit-btn"
                                  @click.stop.prevent="handleKnowitEdit(knowit.knowit_id, groupIndex, knowitIndex)"
                                  type="button"
                                >
                                  <i class="el-icon-edit"></i>
                                </button>
                                <button class="knowit-delete-btn" @click="removeKnowit(groupIndex, knowitIndex)" type="button">
                                  <i class="el-icon-delete"></i>
                                </button>
                              </div>
                            </draggable>

                            <div style="display: flex; gap: 10px; width: 100%;">
                              <button class="knowit-add-button" @click.prevent="addKnowit(groupIndex)" type="button">
                                <i class="el-icon-plus"></i>
                                Add Stack
                              </button>

                              <button
                                v-if="['Pro','Supreme'].includes(creator_functionality_level)"
                                class="knowit-add-button ai-suggest-button"
                                @click.prevent="handleAITask('Group Knowits Suggestion', groupIndex)"
                                type="button"
                              >
                                <i class="el-icon-magic-stick"></i>
                                Get AI Suggestions for this group's stacks
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Only show insert button if not the last group -->
                      <div class="insert-group-container" v-if="groupIndex < collectionForm.groups.length - 1">
                        <button 
                          class="insert-group-button"
                          @click="insertGroup(groupIndex + 1)"
                          type="button"
                        >
                          <i class="el-icon-plus"></i>
                          Add Group Here
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Add Group Button -->
                  <button
                    @click="addGroup"
                    type="button"
                    class="knowit-add-button"
                  >
                      <i class="el-icon-plus"></i>
                    Add New Group
                  </button>
                </div>
              </el-tab-pane>

              <el-tab-pane label="Course Settings" name="settings">
                <div class="settings-flex-row">
                  <div class="settings-container">
                    <div class="setting-item">
                      <span class="setting-label">
                        Entire Course Behind Paywall
                        <el-tooltip
                          class="item"
                          effect="dark"
                          content="When enabled, non-subscribers cannot view any content in this course. They will be prompted to subscribe before entering."
                          placement="top"
                        >
                          <i class="el-icon-info setting-info-icon"></i>
                        </el-tooltip>
                      </span>
                      <el-switch
                        v-model="collectionForm.subscribers_only"
                        active-color="#e6a23c"
                        inactive-color="#dcdfe6"
                      ></el-switch>
                    </div>
                    <el-alert
                      v-if="collectionForm.subscribers_only"
                      title="This entire course is restricted to platform subscribers. Non-subscribers cannot view any content. Group and post-level paywall settings are still applied for finer control."
                      type="warning"
                      :closable="false"
                      show-icon
                      style="margin-bottom: 16px;"
                    ></el-alert>
                    <div class="setting-item">
                      <span class="setting-label">
                        Publicity Status
                        <el-tooltip
                          class="item"
                          effect="dark"
                          content="Current publicity status of your course. Attention: if you take your course private from published, users who have already purchased this course will still be able to read it."
                          placement="top"
                        >
                          <i class="el-icon-info setting-info-icon"></i>
                        </el-tooltip>
                      </span>
                      <div class="status-switch-container">
                        <el-tag 
                          :class="['status-tag', { active: collectionForm.status !== 'PUBLISHED' }]"
                          type="info"
                        >
                          Private
                        </el-tag>
                        <el-switch
                          v-model="isPublished"
                          @change="handleStatusChange"
                          active-color="#13ce66"
                          inactive-color="#ff4949"
                        ></el-switch>
                        <el-tag 
                          :class="['status-tag', { active: collectionForm.status === 'PUBLISHED' }]"
                          type="success"
                        >
                          Public
                        </el-tag>
                      </div>
                    </div>
                    <div class="setting-item">
                      <span class="setting-label">
                        Show in public search?
                        <el-tooltip
                          class="item"
                          effect="dark"
                          content="If turned on, your collection will appear in users' search results or system recommendations"
                          placement="top"
                        >
                          <i class="el-icon-info setting-info-icon"></i>
                        </el-tooltip>
                      </span>
                      <el-switch
                        v-model="collectionForm.settings.shown_in_public_search"
                        active-color="#13ce66"
                        inactive-color="#ff4949">
                      </el-switch>
                    </div>

                  </div>
                  <div class="save-section">
                      <el-button 
                        type="primary" 
                        size="medium"
                        @click="submitForm"
                        :loading="saving"
                      >
                        Save
                      </el-button>
                    </div>
                </div>
              </el-tab-pane>
              <el-tab-pane label="Pricing" name="pricing">
                <div class="pricing-flex-row">
                  <div class="pricing-container">
                    <div class="price-section">
                      <label for="price">Price</label>
                      <input
                        v-model="collectionForm.usd_price"
                        id="price"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Enter price"
                      >
                      <span style="margin-left: 10px; color: #909399">USD</span>
                    </div>
                    <div class="price-section" style="margin-top: 16px;">
                      <label for="base_credits">Credits</label>
                      <input
                        v-model.number="collectionForm.base_credits"
                        id="base_credits"
                        type="number"
                        min="0"
                        step="1"
                        placeholder="Enter base credits"
                      >
                      <span style="margin-left: 10px; color: #909399">
                        Awarded on completion (multiplied by completion score). Suggested: 1 credit per ~30 min of reading.
                      </span>
                    </div>
                  </div>
                  <div class="save-section">
                      <el-button
                        type="primary"
                        size="medium"
                        @click="submitForm"
                        :loading="saving"
                      >
                        Save
                      </el-button>
                    </div>
                </div>
              </el-tab-pane>
              <el-tab-pane label="Test Bank" name="testbank" v-if="isEditing">
                <TestBankManager
                  ref="testBankManager"
                  :collection-id="collectionId"
                  :collection-form="collectionForm"
                  @stats-updated="handleTestBankStatsUpdated"
                />
              </el-tab-pane>
              <el-tab-pane label="Examination" name="examination" v-if="isEditing">
                <ExamConfigurationEditor
                  :collection-id="collectionId"
                  :test-bank-count="testBankCount"
                />
              </el-tab-pane>
              <el-tab-pane label="Exam Analytics" name="exam-analytics" v-if="isEditing">
                <ExamAnalyticsDashboard
                  :collection-id="collectionId"
                />
              </el-tab-pane>
              <el-tab-pane label="Social Cards" name="social-cards">
                <div class="social-cards-container">
                  <p style="color: #909399; font-size: 13px; margin-bottom: 20px;">
                    Live preview of social media cards rendered from your collection data. Right-click any card → "Save image as" or use the download button. All cards are HTML-rendered — no image generation cost.
                  </p>

                  <!-- Gold Quote Cards (5:7 portrait) -->
                  <div v-if="collectionForm.gold_quotes && collectionForm.gold_quotes.length > 0" style="margin-bottom: 32px;">
                    <h4 style="margin-bottom: 12px; font-size: 14px; color: #303133;">Gold Quote Cards <span style="color:#909399; font-weight:normal;">({{ collectionForm.gold_quotes.length }})</span></h4>
                    <div style="display: flex; gap: 16px; flex-wrap: wrap;">
                      <div
                        v-for="(gq, gi) in collectionForm.gold_quotes"
                        :key="'gq-' + gi"
                        :ref="'goldCard' + gi"
                        class="sns-card sns-card-quote"
                      >
                        <div class="sns-card-inner">
                          <div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div>
                          <div class="sns-quote-mark">&ldquo;</div>
                          <div class="sns-quote-insight">{{ gq.insight }}</div>
                          <div class="sns-quote-detail" v-if="gq.detail">{{ gq.detail }}</div>
                          <div class="sns-card-footer">
                            <div class="sns-collection-title">{{ collectionForm.title }}</div>
                            <div class="sns-genre" v-if="collectionForm.genre_tags && collectionForm.genre_tags.length">
                              {{ collectionForm.genre_tags.join(' · ') }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Walkaway Artifacts List Card (4:5) -->
                  <div v-if="collectionForm.walkaway_artifacts && collectionForm.walkaway_artifacts.length > 0" style="margin-bottom: 32px;">
                    <h4 style="margin-bottom: 12px; font-size: 14px; color: #303133;">Walkaway List Card</h4>
                    <div style="display: flex; gap: 16px; flex-wrap: wrap;">
                      <div class="sns-card sns-card-list" :ref="'walkawayCard'">
                        <div class="sns-card-inner">
                          <div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div>
                          <div class="sns-list-title">What You'll Walk Away With</div>
                          <div class="sns-list-items">
                            <div
                              v-for="(line, li) in collectionForm.walkaway_artifacts"
                              :key="'wa-' + li"
                              class="sns-list-item"
                            >
                              <span class="sns-list-number">{{ extractCount(line) }}</span>
                              <span class="sns-list-text">{{ extractRest(line) }}</span>
                            </div>
                          </div>
                          <div class="sns-card-footer">
                            <div class="sns-collection-title">{{ collectionForm.title }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Title Card (1:1) -->
                  <div style="margin-bottom: 32px;">
                    <h4 style="margin-bottom: 12px; font-size: 14px; color: #303133;">Title Card</h4>
                    <div style="display: flex; gap: 16px; flex-wrap: wrap;">
                      <div class="sns-card sns-card-title" :ref="'titleCard'">
                        <div class="sns-card-inner">
                          <div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div>
                          <div class="sns-title-main">{{ collectionForm.title }}</div>
                          <div class="sns-title-sub" v-if="collectionForm.sub_title">{{ collectionForm.sub_title }}</div>
                          <div class="sns-title-genres" v-if="collectionForm.genre_tags && collectionForm.genre_tags.length">
                            <span v-for="(g, gi) in collectionForm.genre_tags" :key="'tg-'+gi" class="sns-genre-pill">{{ g }}</span>
                          </div>
                          <div class="sns-card-footer">
                            <div class="sns-credits" v-if="collectionForm.base_credits">{{ collectionForm.base_credits }} Credits</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Editorial Review Card (4:5) -->
                  <div v-if="collectionForm.editorial_reviews && collectionForm.editorial_reviews.length > 0" style="margin-bottom: 32px;">
                    <h4 style="margin-bottom: 12px; font-size: 14px; color: #303133;">Editorial Review Card</h4>
                    <div style="display: flex; gap: 16px; flex-wrap: wrap;">
                      <div
                        v-for="(er, eri) in collectionForm.editorial_reviews"
                        :key="'er-' + eri"
                        class="sns-card sns-card-review"
                      >
                        <div class="sns-card-inner" style="position: relative;">
                          <div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div>
                          <div class="sns-review-label-corner">Editor's Note</div>
                          <div class="sns-quote-mark">&ldquo;</div>
                          <div class="sns-review-headline">{{ er.headline }}</div>
                          <div class="sns-review-body" v-if="er.body">{{ er.body }}</div>
                          <div class="sns-card-footer">
                            <div class="sns-collection-title">{{ collectionForm.title }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </el-tab-pane>
            </el-tabs>
          </el-form>
        </div>
      </el-main>

      <el-dialog
        title="Confirm Delete"
        :visible.sync="deleteDialogVisible"
        width="400px"
        :modal="false"
      >
        <div>
          <p style="margin-bottom: 20px; font-size: 14px; color: #606266;">Are you sure you want to delete this course?</p>
          <el-radio-group v-model="deleteOption" style="display: flex; flex-direction: column; gap: 12px;">
            <el-radio label="collection_only" style="line-height: 1.8;">
              <span style="font-weight: 500;">Delete course only</span>
              <div style="font-size: 12px; color: #909399; margin-left: 24px; margin-top: 4px;">
                Posts in this course will remain but won't be grouped
              </div>
            </el-radio>
            <el-radio label="collection_and_posts" style="line-height: 1.8;">
              <span style="font-weight: 500; color: #f56c6c;">Delete course and all its posts</span>
              <div style="font-size: 12px; color: #909399; margin-left: 24px; margin-top: 4px;">
                ⚠️ This will permanently delete all posts in this course
              </div>
            </el-radio>
          </el-radio-group>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="deleteDialogVisible = false">Cancel</el-button>
          <el-button type="danger" @click="deleteCollection">Delete</el-button>
        </span>
      </el-dialog>

      <!-- Transfer Collection Dialog -->
      <el-dialog
        title="Transfer Course to Other User"
        :visible.sync="transferDialogVisible"
        width="450px"
        :modal="false"
      >
        <div>
          <p style="margin-bottom: 20px; font-size: 14px; color: #606266;">
            Transfer this course and all its posts to another user by entering their email address.
          </p>
          <el-alert
            title="Warning"
            type="warning"
            :closable="false"
            style="margin-bottom: 20px;"
          >
            <div style="font-size: 13px;">
              This action will transfer ownership of the course and all its posts. You will no longer have access to edit or manage this course after the transfer.
            </div>
          </el-alert>
          <el-form :model="transferForm" :rules="transferRules" ref="transferForm">
            <el-form-item label="User Email" prop="user_email">
              <el-input
                v-model="transferForm.user_email"
                placeholder="Enter the recipient's email address"
                clearable
              ></el-input>
            </el-form-item>
          </el-form>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="transferDialogVisible = false">Cancel</el-button>
          <el-button type="primary" @click="transferCollection" :loading="transferring">Transfer</el-button>
        </span>
      </el-dialog>

      <AIDialog
        :visible.sync="aiDialogVisible"
        :title="aiDialogTitle"
        :task-type="aiTaskType"
        :initial-input="aiDialogInitialInput"
        :context-string="aiContextString"
        @use="handleAIOutput"
      />

      <!-- AI Assistant Drawer for Cover Generation -->
      <el-drawer
        title="AI Collection Cover Generator"
        :visible.sync="aiDrawerVisible"
        direction="rtl"
        size="80%"
        :modal="false"
        :show-close="true"
        :wrapperClosable="false"
        :before-close="handleDrawerClose"
        custom-class="ai-cover-drawer"
      >
        <UserAIDialogue 
          :context_string="getAIContextString()"
          :context_pot="globalContextPot"
          :task-type="currentAITask"
          :current_chain_id="chain_id"
          @cover-generated="handleCoverGenerated"
          @clear-context-pot="clearContextPot"
          @remove-from-pot="removeFromContextPot"
          @use="handleAIOutput"
        />
      </el-drawer>

      <el-dialog
        title="Reorder Groups"
        :visible.sync="reorderDialogVisible"
        width="50%"
      >
        <draggable 
          v-model="tempGroups"
          handle=".group-drag-handle"
          class="reorder-list"
        >
          <div 
            v-for="(group, index) in tempGroups" 
            :key="index"
            class="reorder-item"
          >
            <i class="el-icon-rank group-drag-handle"></i>
            <span class="group-number">Group #{{ index + 1 }}</span>
            <span class="group-title">{{ group.title || 'Untitled Group' }}</span>
          </div>
        </draggable>
        <span slot="footer" class="dialog-footer">
          <el-button @click="reorderDialogVisible = false">Cancel</el-button>
          <el-button type="primary" @click="applyReorder">Apply Changes</el-button>
        </span>
      </el-dialog>

      <!-- Add new dialog component before closing el-container -->
      <el-dialog
        title="AI Content Generation"
        :visible.sync="aiGenerationDialogVisible"
        width="70%"
        :modal="false"
      >
        <div class="dialog-header">
          <el-button
            type="primary"
            @click="confirmArrangeAll"
            style="margin-bottom: 20px;"
          >
            Arrange AI Generation for All
          </el-button>
        </div>
        
        <div v-for="(group, groupIndex) in collectionForm.groups" :key="groupIndex" class="group-section">
          <div class="group-header">
            <h3>{{ group.title || `Group #${groupIndex + 1}` }}</h3>
          </div>
          
          <el-table :data="group.knowits" style="width: 100%">
            <el-table-column label="Knowit" min-width="200">
              <template slot-scope="scope">
                {{ (scope.row.post_info && scope.row.post_info.title) || 'Untitled Knowit' }}
              </template>
            </el-table-column>
            <el-table-column label="Answers" width="100">
              <template slot-scope="scope">
                <span>{{ scope.row.post_info?.answers?.length || 0 }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Generation Status" width="150">
              <template slot-scope="scope">
                <span :class="getStatusClass(scope.row.post_info?.ai_content_generation_status)">
                  {{ scope.row.post_info?.ai_content_generation_status || 'N/A' }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Actions" width="200">
              <template slot-scope="scope">
                <el-button
                  v-if="canArrangeGeneration(scope.row.post_info?.ai_content_generation_status)"
                  size="mini"
                  type="primary"
                  @click="arrangeGeneration(
                    scope.row.knowit_id,
                    groupIndex,
                    scope.$index || scope.row.$index || group.knowits.indexOf(scope.row),
                    scope.row.post_info?.title
                  )"
                >
                  Arrange AI Generation
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-dialog>

      <el-dialog
        title="Batch Delete Knowits"
        :visible.sync="batchDeleteDialogVisible"
        width="600px"
      >
        <div style="max-height: 400px; overflow-y: auto;">
          <el-table
            :data="allKnowitsForBatchDelete"
            border
            @selection-change="handleBatchDeleteSelectionChange"
          >
            <el-table-column type="selection" width="55"></el-table-column>
            <el-table-column label="Group" prop="groupTitle" width="180"></el-table-column>
            <el-table-column label="Knowit Title" prop="knowitTitle"></el-table-column>
          </el-table>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="batchDeleteDialogVisible = false">Cancel</el-button>
          <el-button type="danger" @click="confirmBatchDeleteKnowits" :disabled="batchDeleteSelection.length === 0">Delete Selected</el-button>
        </span>
      </el-dialog>
      
      <!-- AI Assistant Toggle Button at Left Edge -->
      <div 
        class="ai-assistant-toggle"
        @click="toggleAIDrawer"
        :class="{ 'active': aiDrawerVisible }"
      >
        <i class="el-icon-chat-dot-round"></i>
        <span>AI Assistant</span>
      </div>
    </el-container>
  </transition>
</template>

<script>
import { postRequest } from "@/apis/front";
import * as qiniu from "qiniu-js"; // Add qiniu import
import draggable from "vuedraggable";
import AIDialog from "@/components/AIDialog.vue";
import UserAIDialogue from "@/components/UserAIDialogue.vue";
import TestBankManager from "@/components/TestBank/TestBankManager.vue";
import ExamConfigurationEditor from "@/components/Examination/ExamConfigurationEditor.vue";
import ExamAnalyticsDashboard from "@/components/Examination/ExamAnalyticsDashboard.vue";

export default {
  name: "NewCollection",
  components: {
    draggable,
    AIDialog,
    UserAIDialogue,
    TestBankManager,
    ExamConfigurationEditor,
    ExamAnalyticsDashboard,
  },
  data() {
    return {
      availableTracks: [],
      tracksLoading: false,
      creator_functionality_level: this.$store.state.user_info.creator_functionality_level,
      isEditing: false,
      collectionId: "",
      testBankCount: 0,
      deleteDialogVisible: false,
      deleteOption: 'collection_only',
      transferDialogVisible: false,
      transferring: false,
      transferForm: {
        user_email: ''
      },
      transferRules: {
        user_email: [
          { required: true, message: 'Please enter user email', trigger: 'blur' },
          { type: 'email', message: 'Please enter a valid email address', trigger: 'blur' }
        ]
      },
      collectionForm: {
        title: "",
        sub_title: "",
        description: "",
        grand_take_away: "",
        collection_knowledge: "",
        walkaway_artifacts: [],
        author_bio: "",
        track_ids: [],
        gold_quotes: [],
        key_questions: [],
        key_concepts: [],
        sns_cards: [],
        genre_tags: [],
        editorial_reviews: [],
        editorial_brief: {
          who_its_for: "",
          what_stands_out: "",
          read_if: "",
          is_ai_generated: false,
          created_at: 0,
        },
        groups: [],
        status: "PRIVATE",
        usd_price: 0,
        base_credits: 0,
        cover_url: "",
        subscribers_only: false,
        settings: {
          shown_in_public_search: false,
        },
        tags: [], // Changed from string to array
      },
      currentTag: '', // New property for the tag input
      rules: {
        title: [
          { required: true, message: "Please input a title", trigger: "blur" },
          {
            min: 3,
            max: 100,
            message: "Length should be 3 to 100 characters",
            trigger: "blur",
          },
        ],
        description: [
          {
            required: true,
            message: "Please input a description",
            trigger: "blur",
          },
        ],
        groups: [
          {
            type: "array",
            required: true,
            message: "Please add at least one group",
            trigger: "change",
          },
        ],
        usd_price: [
          { required: true, message: "Please input a price", trigger: "blur" },
        ],
        cover_url: [
          {
            required: true,
            message: "Please upload a cover image",
            trigger: "change",
          },
        ],
      },
      activeTab: "info",
      aiDialogVisible: false,
      aiDialogTitle: "",
      aiDialogInitialInput: "",
      aiTaskType: "",
      aiContextString: "",
      aiDrawerVisible: false,
      currentAITask: null,
      reorderDialogVisible: false,
      tempGroups: [],
      containerWidth: '100%',
      defaultWidth: '100%',
      isReady: false,
      aiGenerationDialogVisible: false,
      isPublished: false,
      editGroupIndex: null,
      autoSaveDebounceTimer: null,
      isLoadingData: false, // Flag to prevent auto-save during initial data load
      saving: false,
      selectedKnowits: {}, // { groupIndex: [knowitIndex, ...], ... }
      batchDeleteDialogVisible: false,
      batchDeleteGroupIndex: null,
      batchDeleteSelection: [], // Array of { groupIndex, knowitIndex }
      chain_id: '', // Chain ID for AI task
      selectMode: false,
      selectedGroups: [],
      selectedKnowitsList: [], // Array of {groupIndex, knowitIndex}
    };
  },
  created() {
    console.log('Component created');
    // Hide container initially
    this.isReady = false;

    // Clean up old auto-save caches from other users
    this.$nextTick(() => {
      this.cleanupOldAutoSaveCaches();
    });

    // Get collection ID from store, query, or localStorage (in order of preference)
    const collectionId = this.$store.state.tempCollectionId || this.$route.query.id || localStorage.getItem('editCollectionId');
    if (collectionId) {
      this.isEditing = true;
      this.collectionId = collectionId;
      this.fetchCollectionData(collectionId).then(() => {
        this.checkAutoSavePrompt();
      });
    } else {
      this.checkAutoSavePrompt();
    }

    // Set initial width before mounting
    this.containerWidth = `${this.defaultWidth}px`;
  },
  mounted() {
    console.log('Component mounted');
    setTimeout(() => {
      this.isReady = true;
      // Force a width update after component is ready
      this.updateContainerWidth();
    }, 100);

    // Add resize listener to handle window size changes
    window.addEventListener('resize', this.updateContainerWidth);

    // Load available career/skill tracks so the multi-select has options
    this.fetchAvailableTracks();
  },
  beforeDestroy() {
    // Clean up storage when leaving the page normally
    if (!this.isEditing) {
      localStorage.removeItem("editCollectionId");
    }

    // Clean up the temporary collection ID from store
    this.$store.dispatch('setTempCollectionId', null)

    // Clean up resize listener
    window.removeEventListener('resize', this.updateContainerWidth);
  },
  methods: {
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString();
    },
    async fetchAvailableTracks() {
      this.tracksLoading = true;
      try {
        const response = await postRequest("listCertTracks", {});
        if (response.data && response.data.code === 0) {
          this.availableTracks = response.data.data || [];
        }
      } catch (e) {
        console.error("Failed to load tracks:", e);
      } finally {
        this.tracksLoading = false;
      }
    },
    trackIdString(t) {
      const id = t && t._id;
      if (!id) return "";
      if (typeof id === "string") return id;
      return id.$oid || id.$id || "";
    },
    extractCount(line) {
      const m = (line || '').replace(/\*\*/g, '').match(/^(\d+\+?)\s/);
      return m ? m[1] : '';
    },
    extractRest(line) {
      return (line || '').replace(/\*\*/g, '').replace(/^\d+\+?\s+/, '');
    },
    addGoldQuote() {
      if (!Array.isArray(this.collectionForm.gold_quotes)) {
        this.$set(this.collectionForm, "gold_quotes", []);
      }
      if (this.collectionForm.gold_quotes.length >= 3) return;
      this.collectionForm.gold_quotes.push({
        insight: "",
        detail: "",
        created_at: Math.floor(Date.now() / 1000),
      });
    },
    removeGoldQuote(index) {
      this.collectionForm.gold_quotes.splice(index, 1);
    },
    addWalkawayArtifact() {
      if (!Array.isArray(this.collectionForm.walkaway_artifacts)) {
        this.$set(this.collectionForm, "walkaway_artifacts", []);
      }
      if (this.collectionForm.walkaway_artifacts.length >= 8) return;
      this.collectionForm.walkaway_artifacts.push("");
    },
    removeWalkawayArtifact(index) {
      this.collectionForm.walkaway_artifacts.splice(index, 1);
    },
    addKeyQuestion() {
      if (!Array.isArray(this.collectionForm.key_questions)) {
        this.$set(this.collectionForm, "key_questions", []);
      }
      if (this.collectionForm.key_questions.length >= 5) return;
      this.collectionForm.key_questions.push("");
    },
    removeKeyQuestion(index) {
      this.collectionForm.key_questions.splice(index, 1);
    },
    addKeyConcept() {
      if (!Array.isArray(this.collectionForm.key_concepts)) {
        this.$set(this.collectionForm, "key_concepts", []);
      }
      if (this.collectionForm.key_concepts.length >= 10) return;
      this.collectionForm.key_concepts.push("");
    },
    removeKeyConcept(index) {
      this.collectionForm.key_concepts.splice(index, 1);
    },
    addSnsCard() {
      if (!Array.isArray(this.collectionForm.sns_cards)) {
        this.$set(this.collectionForm, "sns_cards", []);
      }
      this.collectionForm.sns_cards.push({
        card_type: "summary_pack",
        hook: "",
        pages: [""],
        cta: "",
        source_post: "",
        created_at: Math.floor(Date.now() / 1000),
      });
    },
    removeSnsCard(index) {
      this.collectionForm.sns_cards.splice(index, 1);
    },
    addSnsCardPage(cardIndex) {
      this.collectionForm.sns_cards[cardIndex].pages.push("");
    },
    removeSnsCardPage(cardIndex, pageIndex) {
      this.collectionForm.sns_cards[cardIndex].pages.splice(pageIndex, 1);
    },
    addEditorialReview() {
      if (!Array.isArray(this.collectionForm.editorial_reviews)) {
        this.$set(this.collectionForm, "editorial_reviews", []);
      }
      if (this.collectionForm.editorial_reviews.length >= 3) return;
      this.collectionForm.editorial_reviews.push({
        reviewer: "LearningFirst Editorial Team",
        reviewer_role: "",
        headline: "",
        body: "",
        is_ai_generated: true,
        created_at: Math.floor(Date.now() / 1000),
      });
    },
    removeEditorialReview(index) {
      this.collectionForm.editorial_reviews.splice(index, 1);
    },
    handleTestBankStatsUpdated(stats) {
      this.testBankCount = stats?.total || 0;
    },
    async fetchCollectionData(collectionId) {
      // Set flag to prevent auto-save during data load
      this.isLoadingData = true;

      // Show loading modal before making request
      const loadingInstance = this.$loading({
        lock: true,
        text: 'Loading collection...',
        spinner: 'el-icon-loading',
        background: 'rgba(255, 255, 255, 0.7)'
      })
      try {
        const response = await postRequest("getPostCollection", {
          id: collectionId,
          user_id: this.$store.state.user_id,
        });

        if (response.data.code === 0) {
          const collectionData = response.data.data;

          // Verify the collection belongs to the current user
          const currentUserId = this.$store.state.user_id;
          if (collectionData.user_id && collectionData.user_id !== currentUserId) {
            // Collection belongs to a different user, clear stored IDs and reset
            this.$message.warning("This collection belongs to another user. Starting a new collection.");
            localStorage.removeItem('editCollectionId');
            this.$store.dispatch('setTempCollectionId', null);
            this.isEditing = false;
            this.collectionId = '';
            loadingInstance.close();
            return;
          }

          // Set default settings if none exist
          const defaultSettings = {
            shown_in_public_search: false,
            // Add any other default settings here
          };

          // Initialize subscribers_only on groups and knowits if not present
          if (collectionData.groups) {
            collectionData.groups.forEach(group => {
              if (group.subscribers_only === undefined) group.subscribers_only = false;
              if (group.knowits) {
                group.knowits.forEach(knowit => {
                  if (knowit.subscribers_only === undefined) knowit.subscribers_only = false;
                });
              }
            });
          }

          this.collectionForm = {
            ...collectionData,
            sub_title: collectionData.sub_title || collectionData.subtitle || "",
            grand_take_away: collectionData.grand_take_away || "",
            collection_knowledge: collectionData.collection_knowledge || "",
            walkaway_artifacts: Array.isArray(collectionData.walkaway_artifacts) ? collectionData.walkaway_artifacts : [],
            author_bio: collectionData.author_bio || "",
            track_ids: Array.isArray(collectionData.track_ids)
              ? collectionData.track_ids.map(id =>
                  typeof id === "string" ? id : (id && (id.$oid || id.$id)) || ""
                ).filter(Boolean)
              : [],
            gold_quotes: Array.isArray(collectionData.gold_quotes) ? collectionData.gold_quotes : [],
            key_questions: Array.isArray(collectionData.key_questions) ? collectionData.key_questions : [],
            key_concepts: Array.isArray(collectionData.key_concepts) ? collectionData.key_concepts : [],
            sns_cards: Array.isArray(collectionData.sns_cards) ? collectionData.sns_cards : [],
            genre_tags: Array.isArray(collectionData.genre_tags) ? collectionData.genre_tags : [],
            editorial_reviews: Array.isArray(collectionData.editorial_reviews) ? collectionData.editorial_reviews : [],
            editorial_brief: collectionData.editorial_brief || {
              who_its_for: "",
              what_stands_out: "",
              read_if: "",
              is_ai_generated: false,
              created_at: 0,
            },
            base_credits: collectionData.base_credits || 0,
            subscribers_only: collectionData.subscribers_only || false,
            tags: collectionData.tags || [], // This ensures 'tags' is always a reactive array
            settings: collectionData.settings || defaultSettings,
          };
          this.isPublished = collectionData.status === 'PUBLISHED';
        } else {
          throw new Error(response.data.msg || "Failed to load collection");
        }
      } catch (error) {
        this.$message.error("Failed to load course data");
        console.error("Error fetching collection:", error);
        // Clear invalid collection ID
        localStorage.removeItem('editCollectionId');
        this.$store.dispatch('setTempCollectionId', null);
        this.isEditing = false;
        this.collectionId = '';
      }

      // Close loading modal
      loadingInstance.close()

      // Reset flag after a short delay to allow watchers to settle
      this.$nextTick(() => {
        setTimeout(() => {
          this.isLoadingData = false;
        }, 100);
      });
    },
    getGroupsForSync() {
      return this.collectionForm.groups.map(group => ({
        ...group,
        subscribers_only: group.subscribers_only || false,
        knowits: group.knowits.map(knowit => ({
          knowit_id: knowit.knowit_id,
          subscribers_only: knowit.subscribers_only || false
        }))
      }));
    },

    async addGroup() {
        this.collectionForm.groups.push({
          title: "",
          group_sub_title: "",
          knowits: [],
          subscribers_only: false,
        });
        // Only sync to backend if we're editing an existing collection
        if (this.isEditing && this.collectionId) {
          try {
            await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
          } catch (error) {
            console.error('Failed to sync group addition:', error);
          }
        }
      },

    async removeGroup(groupIndex) {
      this.collectionForm.groups.splice(groupIndex, 1);
      await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
    },

    async addKnowit(groupIndex) {
      this.collectionForm.groups[groupIndex].knowits.push({
        knowit_id: "",
      });
      await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
      this.editKnowit(
        "new",
        groupIndex,
        this.collectionForm.groups[groupIndex].knowits.length - 1
      );
    },

    async removeKnowit(groupIndex, knowitIndex) {
      try {
        await this.$confirm(
          'Are you sure you want to remove this knowit?',
          'Warning',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No', 
            type: 'warning'
          }
        );

        // Only runs if user clicks Yes
        this.collectionForm.groups[groupIndex].knowits.splice(knowitIndex, 1);
        await this.updateCollectionPartial({ groups: this.getGroupsForSync() });

      } catch (error) {
        // Only show error if not a cancel
        if (!(error && error.message === 'cancel')) {
          this.$message.error('Failed to remove knowit');
        }
        // If cancel, do nothing
      }
    },
    cleanFormData() {
      return {
        ...this.collectionForm,
        cover_url: this.collectionForm.cover_url,
        subscribers_only: this.collectionForm.subscribers_only || false,
        groups: this.collectionForm.groups.map((group) => ({
          ...group,
          subscribers_only: group.subscribers_only || false,
          knowits: group.knowits.map((knowit) => ({
            knowit_id: knowit.knowit_id,
            subscribers_only: knowit.subscribers_only || false,
            post_info:
              knowit.knowit_id === "" && knowit.post_info
                ? knowit.post_info
                : undefined,
          })),
        })),
      };
    },
    async submitForm() {
      this.saving = true;
      this.$refs.collectionForm.validate(async (valid) => {
        if (valid) {
          try {
            const cleanedData = this.cleanFormData();

            const params = {
              ...cleanedData,
              user_id: this.$store.state.user_id,
            };

            if (this.isEditing) {
              params.id = this.collectionId;
            } else {
              params.id = "";
            }

            //Add check on usd_price
            // Check if collection is public and USD price is not set or zero, show confirmation
            if (this.collectionForm.status === "PUBLISHED") {
              const usdPrice = this.collectionForm.usd_price;
              if (usdPrice === null || usdPrice === undefined || usdPrice === '' || usdPrice === 0) {
                // Show confirmation dialog for free publication
                this.$confirm(
                  'You are about to publish this course for free. Are you sure you want to make it publicly available without charging?',
                  'Confirm Free Publication',
                  {
                    confirmButtonText: 'Yes, Publish for Free',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                  }
                ).then(() => {
                  // User confirmed, continue with submission
                  this.submitToAPI(params);
                }).catch(() => {
                  // User cancelled
                  this.$message.info('Publication cancelled');
                  this.saving = false;
                });
                return;
              }
            }
            
            // If not public or price is set, proceed normally
            await this.submitToAPI(params);
          } catch (error) {
            this.$message.error(error.message || "Operation failed");
          } finally {
            this.saving = false;
          }
        } else {
          this.saving = false;
        }
      });
    },
    async submitToAPI(params) {
      this.saving = true;
      try {
        const response = await postRequest("updatePostCollection", params);

        if (response.data.code === 0) {
          this.clearAutoSaveCache();
          // Remove stored ID after successful submission
          // localStorage.removeItem("editCollectionId");
          if (!this.collectionId) {
            this.collectionId = response.data.data && response.data.data._id && (response.data.data._id.$oid || response.data.data._id.$id);
            localStorage.setItem("editCollectionId", this.collectionId);
            this.isEditing = true;
            
          }
          this.$message.success(
            this.isEditing
              ? "Collection updated successfully!"
              : "Collection created successfully!"
          );
          // this.$router.push("/my-collections");
        } else {
          throw new Error(response.data.msg || "Operation failed");
        }
      } catch (error) {
        this.$message.error(error.message || "Operation failed");
      } finally {
        this.saving = false;
      }
    },
    
    async savePrivate() {
      this.collectionForm.status = "PRIVATE";
      this.submitForm();
    },
    async savePublish() {
      this.collectionForm.status = "PUBLISHED";
      this.submitForm();
    },
    showTransferDialog() {
      this.transferForm.user_email = ''
      this.transferDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.transferForm) {
          this.$refs.transferForm.clearValidate()
        }
      })
    },
    async transferCollection() {
      try {
        // Validate form
        const valid = await this.$refs.transferForm.validate()
        if (!valid) return

        this.transferring = true

        const response = await postRequest("transferCollectionToUser", {
          collection_id: this.collectionId,
          user_email: this.transferForm.user_email,
        })

        if (response.data.code === 0) {
          this.$message.success("Course transferred successfully")
          this.transferDialogVisible = false
          // Go back to collections list after transfer
          setTimeout(() => {
            this.$router.push('/my-collections')
          }, 1000)
        } else {
          throw new Error(response.data.msg || "Failed to transfer collection")
        }
      } catch (error) {
        if (error.message) {
          this.$message.error(error.message)
        } else {
          this.$message.error("Failed to transfer course")
        }
      } finally {
        this.transferring = false
      }
    },
    confirmDelete() {
      this.deleteDialogVisible = true;
    },
    async deleteCollection() {
      try {
        const deletePosts = this.deleteOption === 'collection_and_posts';

        const response = await postRequest("deletePostCollection", {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id,
          delete_posts: deletePosts,
        });

        if (response.data.code === 0) {
          const message = deletePosts
            ? "Course and all its posts deleted successfully"
            : "Course deleted successfully";
          this.$message.success(message);
          this.deleteDialogVisible = false;
          this.$router.go(-1);
        } else {
          throw new Error(response.data.msg || "Failed to delete");
        }
      } catch (error) {
        this.$message.error(error.message || "Failed to delete course");
      }
    },
    cancel() {
      // Remove stored ID when canceling
      localStorage.removeItem("editCollectionId");
      this.$router.go(-1);
    },
    async beforeCoverUpload(file) {
      const isImage = file.type.startsWith("image/");
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isImage) {
        this.$message.error("Cover must be an image file!");
        return false;
      }
      if (!isLt5M) {
        this.$message.error("Cover size can not exceed 5MB!");
        return false;
      }

      try {
        const tokenResponse = await postRequest("getSingaporeQiniuToken", {});
        let token = tokenResponse.data.data;

        // Show preview immediately using FileReader
        const reader = new FileReader();
        reader.onload = (e) => {
          this.collectionForm.cover_url = e.target.result; // Set temporary local preview
        };
        reader.readAsDataURL(file);

        let observable = qiniu.upload(
          file,
          new Date().getTime() + file.name,
          token
        );

        observable.subscribe({
          complete: (res) => {
            let url = "https://ap.knowit.today/" + res.key;
            console.log('URL to update: '+url)
            this.updateCollectionCover(url);
            // this.collectionForm.cover_url = url; // Update with actual uploaded URL
            this.$message.success("Cover uploaded successfully");
          },
          error: (err) => {
            console.error("Upload error:", err);
            this.$message.error("Failed to upload cover");
            this.collectionForm.cover_url = ""; // Clear preview on error
          },
        });
      } catch (error) {
        this.$message.error("Failed to upload cover");
        console.error("Error uploading cover:", error);
      }

      return false; // Prevent default upload
    },

    async uploadBase64ImageAsCover(dataUrl) {
      try {
        // Show a loading message
        this.$message.info('Uploading cover image...');

        // Convert Base64 data URL to Blob
        const base64Response = await fetch(dataUrl);
        const blob = await base64Response.blob();

        // Create a File object from Blob
        const file = new File([blob], `cover_${Date.now()}.png`, { type: blob.type });

        // Get Qiniu upload token
        const tokenResponse = await postRequest("getSingaporeQiniuToken", {});
        const token = tokenResponse.data.data;

        // Upload to Qiniu
        const observable = qiniu.upload(
          file,
          `${Date.now()}_${file.name}`,
          token
        );

        observable.subscribe({
          complete: (res) => {
            const url = "https://ap.knowit.today/" + res.key;
            console.log('Gemini cover uploaded to:', url);
            this.updateCollectionCover(url);
            this.$message.success("Cover image uploaded and applied successfully!");
          },
          error: (err) => {
            console.error("Upload error:", err);
            this.$message.error("Failed to upload cover image");
          },
        });
      } catch (error) {
        console.error("Error uploading Base64 image:", error);
        this.$message.error("Failed to process cover image");
      }
    },

    handleKnowitEdit(knowitId, groupIndex, knowitIndex) {
      if (!knowitId || knowitId.trim() === "") {
        this.editKnowit("", groupIndex, knowitIndex);
        return;
      }

      this.editKnowit(knowitId, groupIndex, knowitIndex);
    },
    editKnowit(knowitId, groupIndex, knowitIndex) {
      try {
        if (this.isEditing) {
          localStorage.setItem("editCollectionId", this.collectionId);
        }

        if (knowitId) {
          localStorage.setItem("editPostId", knowitId);
        }
        console.log("Collection ID:", this.collectionId);
        console.log("Group Index:", groupIndex);
        console.log("Knowit Index:", knowitIndex);
        console.log("Knowit ID:", knowitId);
        console.log(
          "Post Form:",
          this.collectionForm.groups[groupIndex].knowits[knowitIndex].post_info
        );
        this.$router
          .push({
            name: knowitId ? "EditPost" : "NewPost",
            params: {
              collectionId: this.collectionId,
              groupIndex: groupIndex,
              knowitIndex: knowitIndex,
              postId: knowitId,
              postForm:
                this.collectionForm.groups[groupIndex].knowits[knowitIndex]
                  .post_info,
              collectionContextString: JSON.stringify(this.collectionForm),
            },
          })
          .catch((err) => {
            if (err.name !== "NavigationDuplicated") {
              throw err;
            }
          });
      } catch (error) {
        console.error("Navigation error:", error);
      }
    },
    handleAITask(taskType, groupIndex) {
      this.editGroupIndex = groupIndex;
      this.currentAITask = taskType;

      this.openAIDrawer(taskType);
      // Handle Collection Cover Generation with AiffectChain
      
      
      // // Keep existing logic for other AI tasks
      // this.aiDialogTitle = taskType;
      // this.aiTaskType = taskType;

      // // Set initial input based on task type
      // if (taskType === "Group Knowits Suggestion") {
      //   const group = this.collectionForm.groups[groupIndex];
      //   const params = {
      //     collection_id: this.collectionId,
      //     collection_name: this.collectionForm.title,
      //     collection_description: this.collectionForm.description,
      //     group_index: groupIndex,
      //     group_title: group.title,
      //     user_id: this.$store.state.user_id,
      //     collection_contents: this.collectionForm.groups.map(group => ({
      //       group_title: group.title,
      //       knowits: group.knowits.map(knowit => knowit.post_info?.title || 'Untitled Knowit')
      //     })),
      //   };
      //   this.aiContextString = JSON.stringify(params);
      // } else if (
      //   taskType === "Collection Description Polish" ||
      //   taskType === "Collection Structure Generation"
      // ) {
      //   var params = {
      //     collection_id: this.collectionId,
      //     collection_name: this.collectionForm.title,
      //     collection_description: this.collectionForm.description,
      //     user_id: this.$store.state.user_id,
      //   };
      //   this.aiContextString = JSON.stringify(params);
      // }

      // this.aiDialogVisible = true;
    },

    // === Paywall / Access Control Methods ===
    toggleGroupPaywall(groupIndex) {
      const group = this.collectionForm.groups[groupIndex];
      this.$set(group, 'subscribers_only', !group.subscribers_only);
      this.submitForm();
    },
    toggleKnowitPaywall(groupIndex, knowitIndex) {
      const knowit = this.collectionForm.groups[groupIndex].knowits[knowitIndex];
      this.$set(knowit, 'subscribers_only', !knowit.subscribers_only);
      this.submitForm();
    },
    makeAllSubscribersOnly() {
      this.collectionForm.groups.forEach(group => {
        this.$set(group, 'subscribers_only', true);
        group.knowits.forEach(knowit => {
          this.$set(knowit, 'subscribers_only', true);
        });
      });
      this.$message.success('All groups and stacks set to Subscribers Only');
      this.submitForm();
    },
    makeAllFree() {
      this.collectionForm.groups.forEach(group => {
        this.$set(group, 'subscribers_only', false);
        group.knowits.forEach(knowit => {
          this.$set(knowit, 'subscribers_only', false);
        });
      });
      this.$message.success('All groups and stacks set to Free');
      this.submitForm();
    },
    toggleSelectMode() {
      this.selectMode = !this.selectMode;
      if (!this.selectMode) {
        this.selectedGroups = [];
        this.selectedKnowitsList = [];
      }
    },
    toggleGroupSelection(groupIndex) {
      const idx = this.selectedGroups.indexOf(groupIndex);
      if (idx === -1) {
        this.selectedGroups.push(groupIndex);
      } else {
        this.selectedGroups.splice(idx, 1);
      }
    },
    toggleKnowitSelection(groupIndex, knowitIndex) {
      const idx = this.selectedKnowitsList.findIndex(
        k => k.groupIndex === groupIndex && k.knowitIndex === knowitIndex
      );
      if (idx === -1) {
        this.selectedKnowitsList.push({ groupIndex, knowitIndex });
      } else {
        this.selectedKnowitsList.splice(idx, 1);
      }
    },
    isKnowitSelected(groupIndex, knowitIndex) {
      return this.selectedKnowitsList.some(
        k => k.groupIndex === groupIndex && k.knowitIndex === knowitIndex
      );
    },
    selectAll() {
      this.selectedGroups = this.collectionForm.groups.map((_, i) => i);
      this.selectedKnowitsList = [];
      this.collectionForm.groups.forEach((group, gi) => {
        group.knowits.forEach((_, ki) => {
          this.selectedKnowitsList.push({ groupIndex: gi, knowitIndex: ki });
        });
      });
    },
    deselectAll() {
      this.selectedGroups = [];
      this.selectedKnowitsList = [];
    },
    makeSelectedSubscribersOnly() {
      this.selectedGroups.forEach(gi => {
        this.$set(this.collectionForm.groups[gi], 'subscribers_only', true);
      });
      this.selectedKnowitsList.forEach(({ groupIndex, knowitIndex }) => {
        this.$set(this.collectionForm.groups[groupIndex].knowits[knowitIndex], 'subscribers_only', true);
      });
      this.$message.success('Selected items set to Subscribers Only');
      this.submitForm();
    },
    makeSelectedFree() {
      this.selectedGroups.forEach(gi => {
        this.$set(this.collectionForm.groups[gi], 'subscribers_only', false);
      });
      this.selectedKnowitsList.forEach(({ groupIndex, knowitIndex }) => {
        this.$set(this.collectionForm.groups[groupIndex].knowits[knowitIndex], 'subscribers_only', false);
      });
      this.$message.success('Selected items set to Free');
      this.submitForm();
    },
    handleAIOutput(output) {
      if (this.aiDialogTitle === "Collection Description Polish") {
        this.collectionForm.description = output;
      } else if (this.aiDialogTitle === "Collection Structure Generation") {
        console.log("output:", output);

        // Transform the new structure into the required format
        const processedOutput = Object.entries(output).map(
          ([lessonTitle, blocks]) => ({
            title: lessonTitle,
            knowits: blocks.map((blockTitle) => ({
              knowit_id: "",
              post_info: { title: blockTitle },
            })),
          })
        );

        this.collectionForm.groups = processedOutput;
      }else if(this.aiDialogTitle === "Group Knowits Suggestion"){
        // Update group knowits
        this.collectionForm.groups[this.editGroupIndex].knowits = output;
        this.$message.success("AI suggestions applied successfully");
        this.editGroupIndex = null;
      }

      this.$message.success("AI content applied successfully");
    },

    async updateCollectionCover(coverUrl) {
      try {
        // Only make API call if we're editing an existing collection
        if (this.isEditing) {
          const response = await postRequest("updatePostCollectionCover", {
            user_id: this.$store.state.user_id,
            collection_id: this.collectionId,
            cover_url: coverUrl
          });

          if (response.data.code !== 0) {
            throw new Error(response.data.msg || "Failed to update cover");
          }
        }
        
        // Update local state
        this.collectionForm.cover_url = coverUrl;
      } catch (error) {
        this.$message.error(error.message || "Failed to update course cover");
        console.error("Error updating collection cover:", error);
      }
    },
    previewRead() {
      this.$store.dispatch('setTempCollectionId', this.collectionId)
      this.$router.push({
        path: '/read-collection',
        query: {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id
        }
      })
    },
    showReorderDialog() {
      this.tempGroups = [...this.collectionForm.groups];
      this.reorderDialogVisible = true;
    },
    async applyReorder() {
      this.collectionForm.groups = [...this.tempGroups];
      this.reorderDialogVisible = false;
      await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
      this.$message.success('Groups reordered successfully');
    },
    insertGroup(index) {
      this.collectionForm.groups.splice(index, 0, {
        title: "",
        group_sub_title: "",
        knowits: [],
        subscribers_only: false
      });
    },
    startEditingTitle(group) {
      // Set editing flag
      this.$set(group, 'isEditing', true);
      // Wait for DOM update then focus input
      this.$nextTick(() => {
        const input = this.$refs.titleInput;
        if (input && input.length > 0) {
          input[0].focus();
        }
      });
    },
    async finishEditingTitle(group) {
      group.isEditing = false;
      await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
    },
    handleGroupTitleBlur(group, event) {
      const next = event && event.relatedTarget;
      if (next && next.classList && (next.classList.contains('group-title-input') || next.classList.contains('group-subtitle-input'))) {
        return;
      }
      this.finishEditingTitle(group);
    },
    getTotalCardsForGroup(group) {
      // Simply return the number of knowits/cards in the group
      return group.knowits ? group.knowits.length : 0;
    },
    updateContainerWidth() {
      // Force initial width before any calculations
      this.containerWidth = `${this.defaultWidth}px`;
      
      this.$nextTick(() => {
        const container = this.$refs.container;
        if (!container) return;

        const element = container.$el || container;
        if (!element) return;

        // Get the parent width to ensure we don't exceed it
        const parentWidth = (element.parentElement && element.parentElement.offsetWidth) || window.innerWidth;
        const targetWidth = Math.min(this.defaultWidth, parentWidth);
        
        console.log('Width calculations:', {
          defaultWidth: this.defaultWidth,
          parentWidth,
          targetWidth,
          currentWidth: element.offsetWidth
        });

        // Update container width if needed
        this.containerWidth = `${targetWidth}px`;
        element.classList.add('full-width');
      });
    },
    handleEnter(el) {
      console.log('Transition enter started', el);
      if (!el) {
        console.log('No element provided to handleEnter');
        return;
      }
      
      // Force a layout recalculation
      el.offsetHeight;
      
      // Add class to transition element
      el.classList.add('full-width');
    },
    showAIGenerationDialog() {
      this.aiGenerationDialogVisible = true;
    },
    getStatusClass(status) {
      return {
        'status-pending': status === 'PENDING',
        'status-processing': status === 'PROCESSING',
        'status-na': !status
      };
    },
    canArrangeGeneration(status) {
      return !status || (status !== 'PENDING');
    },
    async arrangeGeneration(knowitId,groupIndex=null,postIndex=null,postTitle=null) {
      try {
        const response = await postRequest('pendingPostForAIGeneration', {
          post_id: knowitId,
          collection_id: this.collectionId,
          group_index: groupIndex,
          post_index: postIndex,
          post_title: postTitle,

        });
        
        if (response.data.code === 0) {
          this.$message.success('AI generation arranged successfully');
          const new_post = response.data.data;// Refresh data
          
          this.collectionForm.groups[new_post.group_index].knowits[new_post.post_index].post_info = new_post;
          if(knowitId == ''){
            this.collectionForm.groups[new_post.group_index].knowits[new_post.post_index].knowit_id = (new_post._id.$oid || new_post._id.$id);
          }
        } else {
          throw new Error(response.data.msg || 'Failed to arrange AI generation');
        }
      } catch (error) {
        this.$message.error(error.message);
      }
    },
    async confirmArrangeAll() {
      try {
        await this.$confirm(
          'Are you sure you want to arrange AI generation for all knowits?',
          'Confirm',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning'
          }
        );

        const response = await postRequest('pendingCollectionPostsForAIGeneration', {
          collection_id: this.collectionId
        });

        if (response.data.code === 0) {
          this.$message.success('AI generation arranged for all knowits');
          await this.fetchCollectionData(this.collectionId); // Refresh data
        } else {
          throw new Error(response.data.msg || 'Failed to arrange AI generation');
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(error.message);
        }
      }
    },
    handleStatusChange(value) {
      this.collectionForm.status = value ? 'PUBLISHED' : 'PRIVATE';

      console.log('Collection status:', this.collectionForm.status);
    },
    shareCollection() {
      const url = `test.knowit.today/#/collection?id=${this.collectionId}`;
      navigator.clipboard
        .writeText(url)
        .then(() => {
          this.$message.success("Link copied to clipboard!");
        })
        .catch(() => {
          this.$message.error("Failed to copy link");
        });
    },
    addTag(event) {

      console.log("addTag",event);
      event.preventDefault(); // Prevent form submission on enter
      const newTag = this.currentTag.trim();

      if(this.collectionForm.tags == null){
        this.collectionForm.tags = [];
      }
      
      if (newTag && !(this.collectionForm.tags && this.collectionForm.tags.includes(newTag))) {
        this.collectionForm.tags.push(newTag);
      }
      
      this.currentTag = ''; // Clear the input
    },

    removeTag(index) {
      console.log("removeTag",index);
      this.collectionForm.tags.splice(index, 1);
      console.log("collectionForm.tags",this.collectionForm.tags);
    },

    handleBackspace(event) {
      // If the input is empty and there are tags, remove the last tag
      if (event.target.value === '' && this.collectionForm.tags.length > 0) {
        this.removeTag(this.collectionForm.tags.length - 1);
      }
    },
    async updateCollectionPartial(params) {
      try {
        const response = await postRequest("updatePostCollectionParams", {
          collection_id: this.collectionId,
          params // only the fields you want to update
        });
        if (response.data.code === 0) {
          this.$message.success("Course updated successfully!");
          return response.data.data;
        } else {
          throw new Error(response.data.msg || "Failed to update course");
        }
      } catch (error) {
        this.$message.error(error.message || "Failed to update course");
        throw error;
      }
    },
    getAutoSaveKey() {
      const userId = this.$store.state.user_id || 'anonymous';
      return `collection_auto_save_${userId}_${this.collectionId || 'new'}`;
    },
    cleanupOldAutoSaveCaches() {
      // Clean up old cache entries that don't have user_id prefix
      const oldKey = `collection_auto_save_${this.collectionId || 'new'}`;
      localStorage.removeItem(oldKey);

      // Also clean up any cache entries from other users for this collection
      const currentUserId = this.$store.state.user_id;
      if (!currentUserId) return;

      Object.keys(localStorage).forEach(key => {
        // Match pattern: collection_auto_save_{userId}_{collectionId}
        if (key.startsWith('collection_auto_save_') && key !== this.getAutoSaveKey()) {
          const collectionIdPart = this.collectionId || 'new';
          // If this cache is for the same collection but different user, remove it
          if (key.endsWith(`_${collectionIdPart}`) && !key.includes(`_${currentUserId}_`)) {
            localStorage.removeItem(key);
          }
        }
      });
    },
    saveAutoSaveCache() {
      // Only save non-group fields
      const { title, sub_title, description, grand_take_away, collection_knowledge, walkaway_artifacts, author_bio, editorial_reviews, editorial_brief, track_ids, gold_quotes, key_questions, key_concepts, sns_cards, genre_tags, status, usd_price, base_credits, cover_url, settings, tags, version_identifier } = this.collectionForm;
      const cache = { title, sub_title, description, grand_take_away, collection_knowledge, walkaway_artifacts, author_bio, editorial_reviews, editorial_brief, track_ids, gold_quotes, key_questions, key_concepts, sns_cards, genre_tags, status, usd_price, base_credits, cover_url, settings, tags, version_identifier };
      localStorage.setItem(this.getAutoSaveKey(), JSON.stringify(cache));
    },
    clearAutoSaveCache() {
      localStorage.removeItem(this.getAutoSaveKey());
    },
    loadAutoSaveCache() {
      const cache = localStorage.getItem(this.getAutoSaveKey());
      return cache ? JSON.parse(cache) : null;
    },
    debounceAutoSave() {
      // Skip auto-save during initial data load
      if (this.isLoadingData) return;

      if (this.autoSaveDebounceTimer) clearTimeout(this.autoSaveDebounceTimer);
      this.autoSaveDebounceTimer = setTimeout(() => {
        this.saveAutoSaveCache();
      }, 1000);
    },
    async checkAutoSavePrompt() {
      const cache = this.loadAutoSaveCache();
      if (!cache) return;

      // Check if cache has meaningful data that differs from current form
      const hasValidCache = this.isCacheDifferentFromForm(cache);
      if (!hasValidCache) {
        // Cache is same as current data or empty, clear it
        this.clearAutoSaveCache();
        return;
      }

      try {
        await this.$confirm(
          'An auto-saved version of this course was found. Do you want to load it?',
          'Auto-Save Detected',
          {
            confirmButtonText: 'Load Auto-Saved',
            cancelButtonText: 'Ignore',
            type: 'info'
          }
        );
        // User confirmed, load cache
        Object.assign(this.collectionForm, cache);
        this.$message.success('Auto-saved version loaded!');
      } catch (e) {
        // User cancelled, clear the cache so it doesn't prompt again
        this.clearAutoSaveCache();
      }
    },
    isCacheDifferentFromForm(cache) {
      // Check if cache has any meaningful differences from current form
      const fieldsToCompare = ['title', 'sub_title', 'description', 'grand_take_away', 'collection_knowledge', 'usd_price', 'base_credits', 'cover_url', 'version_identifier', 'editorial_brief', 'author_bio'];

      for (const field of fieldsToCompare) {
        const cacheValue = cache[field] || '';
        const formValue = this.collectionForm[field] || '';

        // Convert to string for comparison
        const cacheStr = String(cacheValue).trim();
        const formStr = String(formValue).trim();

        if (cacheStr !== formStr && cacheStr !== '') {
          // Found a meaningful difference
          return true;
        }
      }

      // No meaningful differences found
      return false;
    },
    showBatchDeleteDialog() {
      this.batchDeleteDialogVisible = true;
      this.batchDeleteSelection = [];
    },
    handleBatchDeleteSelectionChange(selection) {
      this.batchDeleteSelection = selection.map(row => ({
        groupIndex: row.groupIndex,
        knowitIndex: row.knowitIndex
      }));
    },
    async confirmBatchDeleteKnowits() {
      if (!this.batchDeleteSelection.length) {
        this.$message.warning('Please select knowits to delete.');
        return;
      }
      try {
        await this.$confirm(
          `Are you sure you want to delete ${this.batchDeleteSelection.length} knowits?`,
          'Batch Delete',
          { type: 'warning' }
        );
        // Remove from frontend (sort by group and knowit index descending for safe splicing)
        const grouped = {};
        this.batchDeleteSelection.forEach(({ groupIndex, knowitIndex }) => {
          if (!grouped[groupIndex]) grouped[groupIndex] = [];
          grouped[groupIndex].push(knowitIndex);
        });
        Object.keys(grouped).forEach(groupIndex => {
          grouped[groupIndex].sort((a, b) => b - a).forEach(knowitIndex => {
            this.collectionForm.groups[groupIndex].knowits.splice(knowitIndex, 1);
          });
        });
        // Sync to backend
        await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
        this.$message.success('Batch delete successful!');
        this.batchDeleteDialogVisible = false;
        this.batchDeleteSelection = [];
      } catch (e) {
        // Cancelled or error
      }
    },
    
    openAIDrawer(taskType) {
      this.currentAITask = taskType;
      
      // Prepare context pot for this task
      if(taskType === 'Collection Cover Generation') {//OK
        const collectionContext = { ...this.collectionForm, groups: undefined };
        this.prepareContextPot([collectionContext]);
        this.chain_id = '68a7350894c7849b5b8b458f'; // Pass the chain_id to UserAIDialogue
      }else if (taskType === "Collection Description Polish"){//OK
        const collectionContext = { ...this.collectionForm, groups: undefined };
        this.prepareContextPot([collectionContext]);
        this.chain_id = '68a72f8f94c784ae5b8b4590';
      }else if(taskType === "Collection Structure Generation"){//OK
        const collectionContext = { ...this.collectionForm, groups: undefined };
        this.prepareContextPot([collectionContext]);
        this.chain_id = '68a72f8e94c784a85b8b4596';
      
      }else if(taskType === "Group Knowits Suggestion"){//OK
        const collectionContext = { ...this.collectionForm, groups: undefined };
        this.prepareContextPot([collectionContext]);
        this.chain_id = '68a72f8f94c7849d5b8b4594';
      }
      
      this.aiDrawerVisible = true;
    },

    prepareContextPot(elements) {
      // Clear existing context pot
      this.$store.dispatch('contextPot/clearAll');
      
      // Add collection info to context pot 
      elements.forEach(element => {
        this.$store.dispatch('contextPot/addItem', element);
      });
      
      
    },

    clearContextPot() {
      this.$store.dispatch('contextPot/clearAll').then(result => {
        if (result.success) {
          this.$message.success(result.message);
        }
      });
    },

    removeFromContextPot(index) {
      this.$store.dispatch('contextPot/removeItem', index).then(result => {
        if (result.success) {
          this.$message.info(result.message);
        }
      });
    },
    
    handleDrawerClose() {
      this.aiDrawerVisible = false;
      this.currentAITask = null;
    },
    
    toggleAIDrawer() {
      this.aiDrawerVisible = !this.aiDrawerVisible;
      if (this.aiDrawerVisible && !this.currentAITask) {
        // Set a default task when opening via toggle
        this.currentAITask = 'Collection Structure Generation';
        this.$store.dispatch('contextPot/syncItems', [{
          collection_name: this.collectionForm.title || 'New Collection',
          collection_description: this.collectionForm.description || '',
          groups: this.collectionForm.groups || []
        }]);
        this.chain_id = '';
      }
    },
    
    getAIContextString() {
      return JSON.stringify({
        task: this.currentAITask,
        collection_id: this.collectionId,
        collection_title: this.collectionForm.title,
        collection_name: this.collectionForm.title, // Keep for backward compatibility
        collection_description: this.collectionForm.description,
        collection_version: this.collectionForm.version_identifier,
        collection_tags: this.collectionForm.tags,
        collection_groups: this.collectionForm.groups.map(group => ({
          title: group.title,
          knowits_count: group.knowits.length,
          knowits: group.knowits.map(knowit => ({
            title: (knowit.post_info && knowit.post_info.title) || 'Untitled Knowit',
            answers_count: (knowit.post_info && knowit.post_info.answers && knowit.post_info.answers.length) || 0
          }))
        })),
        user_id: this.$store.state.user_id,
      });
    },
    
    
    handleCoverGenerated(coverUrl) {
      if (coverUrl) {
        this.collectionForm.cover_url = coverUrl;
        this.aiDrawerVisible = false;
        this.$message.success('Cover generated and applied successfully!');
        // Auto-save the cover
        this.debounceAutoSave();
      }
    },

    handleAIOutput(result, type) {
      console.log('Received AI result:', result, 'Type:', type);
      
      try {
        switch (type) {
          case 'collectionDescriptionPolish':
            // Apply the polished description to the collection
            this.collectionForm.description = typeof result === 'string' ? result : JSON.stringify(result);
            this.$message.success('Description applied successfully!');
            break;
            
          case 'bundleDescriptionGeneration':
            // Apply the generated description to the collection
            this.collectionForm.description = typeof result === 'string' ? result : JSON.stringify(result);
            this.$message.success('Description generated and applied successfully!');
            break;
            
          case 'createCollectionStructure':
            // Apply the generated structure to the collection
            if (Array.isArray(result)) {
              this.collectionForm.groups = result.map(group => ({
                ...group,
                knowits: group.knowits || []
              }));
            } else if (result.groups && Array.isArray(result.groups)) {
              this.collectionForm.groups = result.groups.map(group => ({
                ...group,
                knowits: group.knowits || []
              }));
            }
            this.$message.success('Collection structure applied successfully!');
            break;
            
          case 'groupKnowitsSuggestion':
            // Apply suggested knowits to the active group
            if (Array.isArray(result) && this.collectionForm.groups.length > 0) {
              const lastGroup = this.collectionForm.groups[this.collectionForm.groups.length - 1];
              const newKnowits = result.map(item => ({
                id: Date.now() + Math.random(),
                post_info: item.post_info,
                status: 'PRIVATE'
              }));
              lastGroup.knowits.push(...newKnowits);
            }
            this.$message.success('Knowits suggestions applied successfully!');
            break;
            
          case 'createCollectionCoverPrompt':
            // Handle cover generation result
            if (typeof result === 'string') {
              if (result.startsWith('http')) {
                // MidJourney URL - use directly
                this.collectionForm.cover_url = result;
                this.$message.success('Cover image applied successfully!');
              } else if (result.startsWith('data:image/')) {
                // Gemini Base64 data URL - need to upload to Qiniu
                this.uploadBase64ImageAsCover(result);
              } else {
                this.$message.info('Cover generation result received');
              }
            }
            break;
            
          case 'general':
          default:
            // For general results, show them to the user
            this.$message.info('AI result received and ready to use');
            console.log('General AI result:', result);
            break;
        }
        
        // Auto-save after applying changes
        this.debounceAutoSave();
        
        // Close the AI drawer
        this.aiDrawerVisible = false;
        
      } catch (error) {
        console.error('Error handling AI output:', error);
        this.$message.error('Failed to apply AI result: ' + error.message);
      }
    },
  },
  computed: {
    globalContextPot() {
      return this.$store.state.contextPot.items;
    },
    totalKnowits() {
      return this.collectionForm.groups.reduce((total, group) => {
        return total + group.knowits.length;
      }, 0);
    },
    allKnowitsForBatchDelete() {
      // Flatten all knowits with group/knowit index for selection
      const result = [];
      this.collectionForm.groups.forEach((group, groupIndex) => {
        group.knowits.forEach((knowit, knowitIndex) => {
          result.push({
            groupIndex,
            knowitIndex,
            groupTitle: group.title || `Group #${groupIndex + 1}`,
            knowitTitle: (knowit.post_info && knowit.post_info.title) || 'Untitled Knowit'
          });
        });
      });
      return result;
    },
  },
  watch: {
    'collectionForm.title': 'debounceAutoSave',
    'collectionForm.sub_title': 'debounceAutoSave',
    'collectionForm.description': 'debounceAutoSave',
    'collectionForm.status': 'debounceAutoSave',
    'collectionForm.usd_price': 'debounceAutoSave',
    'collectionForm.cover_url': 'debounceAutoSave',
    'collectionForm.settings': {
      handler: 'debounceAutoSave',
      deep: true
    },
    'collectionForm.tags': {
      handler: 'debounceAutoSave',
      deep: true
    },
    'collectionForm.version_identifier': 'debounceAutoSave',
    'collectionForm.subscribers_only': 'debounceAutoSave',
    // Do NOT watch groups for auto-save
  },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* AI Cover Drawer Styles */
>>> .ai-cover-drawer {
  z-index: 2000 !important;
}

>>> .el-drawer__wrapper {
  z-index: 2000 !important;
}

>>> .ai-cover-drawer .el-drawer__body {
  padding: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}



.new-collection-container {
  min-width: 100% !important; /* Set minimum width */
  max-width: 100vw !important; /* Prevent overflow */
  width: 100% !important; /* Set default width */
  margin: 0 auto !important;
  padding: 20px;
  height: 100vh;
  overflow-y: auto;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  box-sizing: border-box;
}

.el-container {
  margin: 0 auto !important;
}

.el-header {
  text-align: left;
  padding: 20px 0;
  background: white;
  color: #303133;
  margin-bottom: 20px;
  border-bottom: 2px solid #409eff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.el-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0.5px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color: #303133;
}

.el-main {
  padding: 20px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: 0 20px;
}

.button-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.button-group .el-button {
  margin: 0;
}

.groups-container {
  width: 100%;
  margin: 20px 0;
}

.groups-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.group-wrapper {
  position: relative;
}

.group-card {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.group-card-header {
  background-color: #f0f7ff;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  border-bottom: 1px solid #e6e6e6;
}

.group-card-content {
  padding: 20px;
}

.group-number {
  font-weight: 600;
  color: #606266;
  min-width: 100px;
}

.group-title-input {
  flex: 1;
  height: 36px;
  padding: 0 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  background-color: white;
  transition: all 0.3s ease;
}

.group-title-input:hover {
  border-color: #409eff;
}

.group-title-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.group-title-input::placeholder {
  color: #c0c4cc;
}

.delete-group-btn {
  padding: 7px !important;
}

.paywall-toggle-btn {
  padding: 7px !important;
}

.knowit-lock-btn {
  background: none;
  border: 1px solid #dcdfe6;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #c0c4cc;
  transition: all 0.2s;
  flex-shrink: 0;
  margin-right: 4px;
}

.knowit-lock-btn:hover {
  border-color: #e6a23c;
  color: #e6a23c;
}

.knowit-lock-btn.is-locked {
  background-color: #fdf6ec;
  border-color: #e6a23c;
  color: #e6a23c;
}

.knowit-lock-btn.is-inherited {
  opacity: 0.5;
  cursor: not-allowed;
}

.knowit-lock-btn:disabled {
  cursor: not-allowed;
}

.knowit-list {
  padding: 10px 0;
}

.knowit-item {
  display: inline-flex;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.knowit-item:hover {
  background: #f0f2f5;
}

.knowit-number {
  width: 100px;
  display: inline-block;
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  padding-left: 10px;
}

.knowit-title {
  width: calc(100% - 200px);
  margin: 0 10px;
}

.knowit-delete-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f56c6c;
  border: none;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.knowit-delete-btn:hover {
  background: #f78989;
  transform: scale(1.05);
}

.knowit-delete-btn:active {
  transform: scale(0.95);
}

.groups-container :deep(.add-group-btn) {
  display: block;
  width: 100%;
  margin: 20px 0;
  height: auto;
  padding: 15px 20px;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.groups-container :deep(.add-group-btn:hover) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(103, 194, 58, 0.3);
}

.groups-container :deep(.add-group-btn:active) {
  transform: translateY(1px);
  box-shadow: 0 2px 4px rgba(103, 194, 58, 0.2);
}

.cover-upload {
  text-align: center;
}

.cover-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 300px;
  height: 424px;
  display: inline-block;
}

.cover-uploader:hover {
  border-color: #409eff;
}

.cover-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  margin-bottom: 8px;
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  pointer-events: none;
}

.cover-clickable {
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.cover-uploader :deep(.el-upload) {
  width: 100%;
  height: 100%;
  display: block;
}

.cover-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

.top-section {
  display: flex;
  gap: 20px;
}

.cover-section {
  flex: 1;
}

.details-section {
  flex: 2;
}

.cover-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  position: absolute;
  width: 100%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.cover-label {
  font-size: 16px;
  color: #8c939d;
}

.no-margin {
  margin: 0 !important;
}

.el-form-item.no-margin .el-form-item__content {
  margin-left: 0 !important;
}

.title-section {
  margin-bottom: 20px;
}

.title-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #606266;
}

.title-section input {
  width: 90%;
  height: 40px;
  padding: 0 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  transition: border-color 0.2s;
}

.title-section input:hover {
  border-color: #c0c4cc;
}

.title-section input:focus {
  outline: none;
  border-color: #409eff;
}

.description-section {
  margin-bottom: 20px;
}

.description-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #606266;
}

.description-section textarea {
  width: 90%;
  padding: 10px 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  transition: border-color 0.2s;
  resize: vertical;
  min-height: 160px;
}

.description-section textarea:hover {
  border-color: #c0c4cc;
}

.description-section textarea:focus {
  outline: none;
  border-color: #409eff;
}

.price-section {
  margin-bottom: 20px;
}

.price-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #606266;
}

.price-section input {
  width: 150px;
  height: 40px;
  padding: 0 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  transition: border-color 0.2s;
}

.price-section input:hover {
  border-color: #c0c4cc;
}

.price-section input:focus {
  outline: none;
  border-color: #409eff;
}

.knowit-add-button {
  width: 100%;
  height: 40px;
  margin: 10px 0;
  background-color: #f5f7fa;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  color: #409eff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.knowit-add-button:hover {
  background-color: #ecf5ff;
  border-color: #409eff;
}

.knowit-add-button:active {
  transform: scale(0.98);
}

.knowit-add-button i {
  font-size: 16px;
}

.content-header {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.content-actions {
  display: flex;
  gap: 10px;
}

.content-stats {
  display: flex;
  gap: 20px;
  margin-top: 10px;
}

.stat-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #606266;
  font-size: 14px;
}

.stat-item i {
  font-size: 16px;
  color: #409eff;
}

.ai-structure-btn {
  display: flex;
  align-items: center;
  gap: 5px;
}

.ai-structure-btn i {
  font-size: 14px;
}

.drag-handle {
  cursor: move;
  padding: 0 10px;
  color: #909399;
}

.drag-handle:hover {
  color: #409eff;
}

.knowit-edit-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #409eff;
  border: none;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  margin: 0 8px;
}

.knowit-edit-btn:hover {
  background: #66b1ff;
  transform: scale(1.05);
}

.knowit-edit-btn:active {
  transform: scale(0.95);
}

.settings-container {
  padding: 20px;
  min-height: 300px;
}

.setting-item {
  display: flex;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ebeef5;
}

.setting-label {
  flex: 1;
  font-size: 14px;
  color: #606266;
}

.setting-info-icon {
  margin-left: 5px;
  color: #909399;
  font-size: 14px;
  cursor: help;
}

.setting-info-icon:hover {
  color: #409eff;
}

.el-radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.description-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.ai-polish-btn {
  padding: 5px 10px;
  font-size: 12px;
}

.ai-polish-btn i {
  margin-right: 4px;
}

.pricing-container {
  padding: 20px;
  max-width: 600px;
}

.price-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
}

.reorder-list {
  min-height: 200px;
}

.reorder-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  margin-bottom: 8px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}

.reorder-item:hover {
  background: #f0f2f5;
}

.group-drag-handle {
  cursor: move;
  padding: 0 10px;
  color: #909399;
}

.group-drag-handle:hover {
  color: #409eff;
}

.group-number {
  font-weight: 500;
  margin-right: 15px;
  color: #606266;
  width: 100px;
}

.group-title {
  flex: 1;
  color: #303133;
}

.insert-group-container {
  position: relative;
  height: 0;
  overflow: visible;
  display: flex;
  justify-content: center;
  opacity: 0;  /* Start hidden */
  transition: opacity 0.2s ease;
}

.insert-group-container:hover {
  opacity: 1;  /* Show on hover of the container itself */
}

.group-wrapper:hover .insert-group-container,
.groups-list:hover > .insert-group-container:first-child {
  opacity: 1;  /* Show on hover of either parent */
}

.insert-group-button {
  position: absolute;
  top: -10px;
  z-index: 1;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.insert-group-button:hover {
  background-color: #66b1ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.insert-group-button:active {
  transform: translateY(0);
}

.insert-group-button i {
  font-size: 14px;
}

.group-title-label {
  flex: 1;
  padding: 8px 15px;
  font-size: 18px;
  color: #606266;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.group-title-label:hover {
  background-color: #f5f7fa;
}

.group-subtitle-label {
  font-size: 14px;
  font-weight: 400;
  font-style: italic;
  color: #909399;
  margin-left: 4px;
}

.full-width {
  width: 100% !important;
  max-width: 100vw !important;
  margin: 0 auto !important;
}

/* Ensure the container respects parent bounds */
@media screen and (max-width:100%) {
  .new-collection-container {
    min-width: 100% !important;
    width: 100% !important;
  }
}

.status-pending {
  color: #e6a23c;
}

.status-processing {
  color: #409eff;
}

.status-na {
  color: #909399;
}

.dialog-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.group-section {
  margin-bottom: 30px;
}

.group-header {
  background: #f5f7fa;
  padding: 12px 20px;
  border-radius: 4px;
  margin-bottom: 15px;
}

.group-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
  font-weight: 500;
}

/* Update dialog styles to remove shadow immediately */
:deep(.el-dialog__wrapper) {
  background: rgba(0, 0, 0, 0.2) !important;
  transition: none !important;
}

:deep(.el-dialog) {
  box-shadow: none !important;
  border: 1px solid #ebeef5;
  margin-top: 0 !important;
  transition: none !important;
}

:deep(.el-dialog__wrapper[aria-hidden="true"]) {
  background: none !important;
}

:deep(.v-modal) {
  opacity: 0 !important;
  background: none !important;
}

.status-switch-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-tag {
  opacity: 0.5;
  transition: all 0.3s ease;
}

.status-tag.active {
  opacity: 1;
  font-weight: bold;
}

:deep(.el-switch) {
  height: 22px;
}

.ai-suggest-button {
  background-color: #ecf5ff;
  border-color: #409eff;
  color: #409eff;
}

.ai-suggest-button:hover {
  background-color: #d9ecff;
  border-color: #409eff;
}

.version-section {
  margin-bottom: 20px;
}

.version-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #606266;
}

.version-section input {
  width: 90%;
  height: 40px;
  padding: 0 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  transition: border-color 0.2s;
}

.version-section input:hover {
  border-color: #c0c4cc;
}

.version-section input:focus {
  outline: none;
  border-color: #409eff;
}

.cover-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.ai-generate-btn {
  width: 100%;
  max-width: 300px;
}

.tag-section {
  /* This ensures the entire section is a block and respects vertical margins */
  display: block; 
  margin-bottom: 20px; /* Adds space below the whole section */
}

.tag-section label {
  display: block; /* Make sure the label is a block */
  margin-bottom: 8px; /* This will now create the 8px space you want */
  font-weight: 500;
  color: #606266;
}

.tags-input-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 0px 10px;
  width: 90%;
  height:40px;
  cursor: text;
}

.tags-input-container:focus-within {
  border-color: #409eff;
}

.tag-item {
  display: inline-flex;
  align-items: center;
  background-color: #f0f2f5;
  color: #606266;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
}

.remove-tag-btn {
  background: none;
  border: none;
  color: #909399;
  font-size: 16px;
  font-weight: bold;
  margin-left: 6px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.remove-tag-btn:hover {
  color: #303133;
}

.tags-input-container input {
  flex-grow: 1;
  border: none;
  outline: none;
  padding: 4px 0;
  background-color: transparent;
  width: 120px;
}

.settings-flex-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start; /* or center if you want vertical centering */
  gap: 32px; /* space between settings and save button */
}

.settings-container {
  flex: 1 1 auto;
}

.save-section {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start; /* or center */
  margin-left: 24px; /* optional, for extra spacing */
}

.pricing-flex-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start; /* or center for vertical centering */
  gap: 32px; /* space between pricing and save button */
}

.pricing-container {
  flex: 1 1 auto;
}

.save-section {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start; /* or center */
  margin-left: 24px; /* optional, for extra spacing */
}

/* AI Assistant Toggle Button Styles */
.ai-assistant-toggle {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 8px;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: -2px 2px 10px rgba(0, 0, 0, 0.2);
  writing-mode: vertical-rl;
  text-orientation: mixed;
}

.ai-assistant-toggle:hover {
  padding-left: 12px;
  padding-right: 12px;
  box-shadow: -3px 3px 15px rgba(0, 0, 0, 0.3);
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.ai-assistant-toggle.active {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  transform: translateY(-50%) translateX(-5px);
}

.ai-assistant-toggle i {
  font-size: 20px;
  writing-mode: horizontal-tb;
}

.ai-assistant-toggle span {
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1px;
}

/* Ensure the toggle button appears above other elements */
.new-collection-container {
  position: relative;
}

/* ── Social Cards ────────────────────────────────────────────── */
.social-cards-container {
  padding: 16px 0;
}

.sns-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  background: #ffffff;
  flex-shrink: 0;
}

.sns-card-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 28px;
  box-sizing: border-box;
}

/* Universal 5:7 portrait ratio for all card types */
.sns-card-quote,
.sns-card-list,
.sns-card-review,
.sns-card-title {
  width: 280px;
  aspect-ratio: 5 / 7;
}

.sns-card-quote {
  background: linear-gradient(165deg, #fffbf0 0%, #ffffff 40%, #f8faff 100%);
  border: 1px solid #f0e6d2;
}

.sns-card-list,
.sns-card-review {
  background: linear-gradient(165deg, #f8faff 0%, #ffffff 50%, #fafafa 100%);
  border: 1px solid #e7ecf3;
}

.sns-card-title {
  background: linear-gradient(135deg, #1159AF 0%, #1a2a4a 100%);
  border: none;
}
.sns-card-title .sns-card-inner {
  justify-content: center;
  align-items: center;
  text-align: center;
}
.sns-card-title .sns-brand-tag {
  color: rgba(255,255,255,0.5);
}
.sns-card-title .sns-title-main {
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.3;
  margin-bottom: 8px;
}
.sns-card-title .sns-title-sub {
  font-size: 13px;
  color: rgba(255,255,255,0.75);
  line-height: 1.5;
  margin-bottom: 16px;
}
.sns-card-title .sns-genre-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.3);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255,255,255,0.7);
  margin: 0 3px;
}
.sns-card-title .sns-credits {
  color: #fbbf24;
  font-weight: 700;
  font-size: 13px;
}
.sns-card-title .sns-card-footer {
  margin-top: auto;
  padding-top: 16px;
}

/* Brand tag */
.sns-brand-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #409EFF;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.sns-logo-icon {
  width: 14px;
  height: 14px;
  object-fit: contain;
}

/* Editor's Note corner label (top-right) */
.sns-review-label-corner {
  position: absolute;
  top: 28px;
  right: 28px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #409EFF;
}

/* Quote card specifics */
.sns-quote-mark {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 48px;
  line-height: 1;
  color: rgba(64, 158, 255, 0.2);
  margin-bottom: 4px;
}
.sns-quote-insight {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.45;
  color: #1a2332;
  margin-bottom: 12px;
}
.sns-quote-detail {
  font-size: 11px;
  line-height: 1.6;
  color: #8895a6;
}

/* List card specifics */
.sns-list-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a2332;
  margin-bottom: 16px;
}
.sns-list-items {
  flex: 1;
}
.sns-list-item {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f2f5;
}
.sns-list-item:last-child {
  border-bottom: none;
}
.sns-list-number {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 22px;
  font-weight: 700;
  color: #409EFF;
  min-width: 28px;
  text-align: right;
  line-height: 1;
}
.sns-list-text {
  font-size: 12px;
  line-height: 1.45;
  color: #303133;
}

/* Review card specifics */
.sns-review-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #409EFF;
  margin-bottom: 12px;
}
.sns-review-headline {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  color: #1a2332;
  margin-bottom: 10px;
}
.sns-review-body {
  font-size: 11px;
  line-height: 1.6;
  color: #8895a6;
}
.sns-reviewer {
  font-size: 11px;
  font-weight: 600;
  color: #409EFF;
  margin-bottom: 4px;
}

/* Shared footer */
.sns-card-footer {
  margin-top: auto;
  padding-top: 12px;
}
.sns-collection-title {
  font-size: 10px;
  color: #c0c4cc;
  font-weight: 500;
}
.sns-genre {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #c0c4cc;
  margin-top: 2px;
}
</style> 